/**
 * PBFCM-PDP Controller
 * Black Friday Campaign - Mobile Product Detail Page JavaScript Controller
 *
 * Handles all user interactions and state management for PBFCM-PDP template
 * Based on patterns from the former separate-buy-buttons-v4-no-bulk.liquid (removed 2026-09-10)
 */

// Initialize global namespace
window.PBFCM_PDP = window.PBFCM_PDP || {};

(function(PBFCM_PDP) {
  'use strict';

  // Configuration - All settings and constants
  const CONFIG = {
    // Selling Plans
    STAY_SELLING_PLAN_ID: null, // Will be set from hidden field
    ADD_ON_SUBSCRIPTION_PLAN_ID: null,

    // Prices and Discounts (will be updated from product data)
    SUBSCRIPTION_DISCOUNT: 0.64,
    PLAN_UNIT_PRICE: null, // Selling-plan allocation unit price (dollars); used as cart compare
    SHIPPING_COST: 5.00,
    FREE_SHIPPING_THRESHOLD: 50.00,
    BFCM_MODE_ENABLED: false,
    BFCM_DISCOUNT_PERCENT: 0,
    BFCM_DISCOUNT_MULTIPLIER: 1,

    // Product Properties (matches separate-buy-buttons-v4-no-bulk pattern)
    PROPERTIES: {
      FULL_PRICE: "_full_price",
      PRODUCT_TYPE: "_product_type",
      SUBSCRIPTION_PURCHASE: "_is_subscription_purchase",
      ONE_TIME_PURCHASE: "_is_one_time_purchase",
      UPSELL_ITEM: "_is_upsell",
      BFCM_ADD_ON_PROMO_PRICE: "_bfcm_add_on_promo_price",
      BFCM_ADD_ON_FULL_PRICE: "_bfcm_add_on_full_price",
      OTP_DISCOUNT_CODE: "_otp_discount_code",
      SUB_DISCOUNT_CODE: "_sub_discount_code",
      AUTO_APPLY_OTP_DISCOUNT_CODE: "_auto_apply_otp_discount_code",
      UPSELL_DISCOUNT_CODE: "_upsell_discount_code",
      OTP_DISCOUNT_PERCENTAGE: "_otp_discount_percentage",
      SUB_DISCOUNT_PERCENTAGE: "_sub_discount_percentage",
      // Plan a line *would* convert to if upgraded to subscription. Stamped on
      // OTP lines too, so the "_if_upgrade_" prefix keeps it from reading as an
      // active subscription binding (the real binding is item.selling_plan).
      IF_UPGRADE_SELLING_PLAN_ID: "_if_upgrade__selling_plan_id",
      QTY_PER_CHANGE: "_qtyPerChange",
      PLAN_UPGRADE: "_plan_upgrade",
      PLAN_UPGRADE_TO: "_plan_upgrade_to",
      PLAN_UPGRADE_SAVINGS: "_plan_upgrade_savings",
      PLAN_UPGRADE_CODE: "_plan_upgrade_code",
      PLAN_UPGRADE_DISCOUNT_PERCENTAGE: "_plan_upgrade_discount_percentage"
    },

    // Element IDs
    IDS: {
      FREQUENCY_MONTHLY: 'PBFCM-frequency-monthly',
      FREQUENCY_ONETIME: 'PBFCM-frequency-onetime',
      RADIO_MONTHLY: 'PBFCM-radio-monthly',
      RADIO_ONETIME: 'PBFCM-radio-onetime',
      CHECKMARK_MONTHLY: 'PBFCM-checkmark-monthly',
      CHECKMARK_ONETIME: 'PBFCM-checkmark-onetime',
      SELECTED_QUANTITY: 'PBFCM-selected-quantity',
      VARIANT_ID: 'PBFCM-variant-id',
      PRODUCT_ID: 'PBFCM-product-id',
      SELLING_PLAN_ID: 'PBFCM-selling-plan-id',
      ADDON_PLAN_ID: 'PBFCM-addon-plan-id',
      BASE_PRICE: 'PBFCM-base-price',
      SUBSCRIPTION_DISCOUNT: 'PBFCM-subscription-discount',
      SUBSCRIPTION_INTERVAL: 'PBFCM-subscription-interval-select',
      PLAN_SPECIFIC_SUB_DISCOUNT: 'PBFCM-plan-specific-sub-discount',
      ADD_TO_CART_BTN: 'PBFCM-add-to-cart-btn',
      CART_LOADING: 'PBFCM-cart-loading',
      COUPON_CODE: 'PBFCM-coupon-code',
      COPY_MESSAGE: 'PBFCM-copy-message',
      BFCM_MODE: 'PBFCM-bfcm-mode-enabled',
      BFCM_DISCOUNT_PERCENT: 'PBFCM-bfcm-discount-percent',
      STRAIGHT_TO_CHECKOUT: 'PBFCM-straight-to-checkout'
    },

    // Class Names
    CLASSES: {
      OPTION_SELECTED: 'PBFCM-PDP-frequency-selector__option--selected',
      QUANTITY_SELECTED: 'PBFCM-PDP-quantity-selector__item--selected',
      SAVINGS_SELECTED: 'PBFCM-PDP-quantity-selector__savings-badge--selected'
    }
  };

  // State management
  const state = {
    frequency: 'monthly', // 'monthly' or 'onetime'
    quantity: 2, // Default quantity (qty-selector value)
    blockQty: 1, // Per-option block qty multiplier (from data-block-qty)
    optionAtcCoupon: '', // Per buy-box ATC coupon (stamped on line at add)
    optionDisplayDiscountPercent: null, // Per buy-box FE-only display %
    upsells: {}, // { productId: quantity }
    prices: {
      base: 0,
      subscription: 0,
      onetime: 0,
      shipping: CONFIG.SHIPPING_COST,
      total: 0,
      savings: 0
    },
    variantId: null,
    productId: null,
    sellingPlanId: null,
    welcomeGiftIds: []
  };
  let upsellDialogModeObserver = null;
  let upsellDialogModeRefreshFrame = null;

  function getUpsellDialogModeState() {
    const liquidDialogMode = document.getElementById('PBFCM-upsell-dialog-mode')?.value === 'true';
    const pdpUpsellEl = document.querySelector('.pdp-upsell-ab-target');
    const pdpGiftEl = document.querySelector('.pdp-gift-ab-target');
    const isPdpHiddenByCSS = (pdpUpsellEl && getComputedStyle(pdpUpsellEl).display === 'none') ||
      (pdpGiftEl && getComputedStyle(pdpGiftEl).display === 'none');

    return {
      liquidDialogMode,
      isPdpHiddenByCSS,
      useUpsellDialog: liquidDialogMode || isPdpHiddenByCSS
    };
  }

  const pageTemplateMatches = new Map();

  function isPageTemplate(templateSuffix) {
    const expectedTemplate = String(templateSuffix || '').trim();
    if (!expectedTemplate) return false;
    if (!pageTemplateMatches.has(expectedTemplate)) {
      pageTemplateMatches.set(expectedTemplate, matchesPageTemplate(expectedTemplate));
    }
    return pageTemplateMatches.get(expectedTemplate);
  }

  function matchesPageTemplate(expectedTemplate) {
    const templateSources = [
      window.PBFCM_PDP_TEMPLATE,
      window.Shopify?.template,
      window.ShopifyAnalytics?.meta?.page?.pageType,
      document.documentElement?.dataset?.template,
      document.documentElement?.dataset?.templateSuffix,
      document.documentElement?.dataset?.pageTemplate,
      document.body?.dataset?.template,
      document.body?.dataset?.templateSuffix,
      document.body?.dataset?.pageTemplate,
      document.querySelector('[data-template]')?.dataset?.template,
      document.querySelector('[data-template-suffix]')?.dataset?.templateSuffix,
      document.querySelector('[data-page-template]')?.dataset?.pageTemplate,
      document.querySelector('meta[name="template"]')?.content,
      document.querySelector('meta[name="page-template"]')?.content
    ]
      .filter(Boolean)
      .map((value) => String(value).trim());

    const bodyClassMatches = document.body && Array.from(document.body.classList).some((className) => (
      className === expectedTemplate ||
      className === `template-${expectedTemplate}` ||
      className === `template--${expectedTemplate}` ||
      className === `page-${expectedTemplate}` ||
      className === `page-template-${expectedTemplate}`
    ));

    return Boolean(bodyClassMatches) || templateSources.some((value) => (
      value === expectedTemplate ||
      value.endsWith(`.${expectedTemplate}`) ||
      value.includes(expectedTemplate)
    ));
  }

function updateAddToCartButtonText() {
  const buttons = [
    document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN),
    ...document.querySelectorAll('[data-frequency-add-to-cart]')
  ].filter(Boolean);

  const { useUpsellDialog } = getUpsellDialogModeState();

  const isTryNowPage =
    window.location.pathname.includes('50-off') ||
    window.location.pathname.includes('bundle-offer');

const isBtsMasterOtpTemplate =
  isPageTemplate('lp-bts-master-30-90-otp') ||
  isPageTemplate('lp-90-30-otp-v2');
  
  buttons.forEach((button) => {
    const buttonText = button.querySelector(
      '.PBFCM-PDP-cart-summary__button-text'
    );
    if (!buttonText) return;

    const defaultText =
      button.dataset.defaultText || buttonText.textContent.trim();
    let nextText = defaultText;

    if (isBtsMasterOtpTemplate) {
      nextText = 'Try Now & Claim Gifts';
    } else if (isTryNowPage) {
      nextText = 'Try Now';
    } else if (useUpsellDialog) {
      nextText = 'Add To Cart & View Gift';
    }

    button.dataset.defaultText = defaultText;
    buttonText.textContent = nextText;
    button.setAttribute('aria-label', nextText);
  });
}

  function scheduleAddToCartButtonTextUpdate() {
    if (upsellDialogModeRefreshFrame) {
      cancelAnimationFrame(upsellDialogModeRefreshFrame);
    }

    upsellDialogModeRefreshFrame = requestAnimationFrame(() => {
      upsellDialogModeRefreshFrame = null;
      updateAddToCartButtonText();
    });
  }

  function isStyleNode(node) {
    if (node.nodeType !== 1) return false;
    if (node.nodeName === 'STYLE') return true;
    if (node.nodeName === 'LINK') return /\bstylesheet\b/i.test(node.rel || '');
    return Boolean(node.querySelector?.('style, link[rel~="stylesheet"]'));
  }

  // Only CSS can change what getUpsellDialogModeState reads, so script, prefetch and meta insertions are skipped.
  function canChangeUpsellDialogMode(record) {
    if (record.type !== 'childList') return true;
    if (record.target.nodeName === 'STYLE' || record.target.closest?.('style')) return true;
    return [...record.addedNodes, ...record.removedNodes].some(isStyleNode);
  }

  function observeUpsellDialogModeChanges() {
    if (upsellDialogModeObserver) {
      upsellDialogModeObserver.disconnect();
    }

    upsellDialogModeObserver = new MutationObserver((records) => {
      if (records.some(canChangeUpsellDialogMode)) {
        scheduleAddToCartButtonTextUpdate();
      }
    });

    if (document.head) {
      upsellDialogModeObserver.observe(document.head, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    const pdpUpsellEl = document.querySelector('.pdp-upsell-ab-target');
    const pdpGiftEl = document.querySelector('.pdp-gift-ab-target');

    [pdpUpsellEl, pdpGiftEl].forEach((element) => {
      if (!element) return;

      upsellDialogModeObserver.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    });
  }

  /**
   * Initialize the controller
   */
  function init() {

    // Load configuration from hidden fields
    loadConfig();

    // Move simple/OTP options below ATC when configured
    relocateOtpAfterAtc();

    // Sync configurable frequency option (if present)
    const selectedOpt = getSelectedFrequencyOption();
    if (selectedOpt) {
      selectFrequencyOption(selectedOpt, { suppressAutoAtc: true });
    }

    // Auto-fetch / interval dropdown: sync plan + stacked discount
    syncSubscriptionIntervalFromDom();

    // Calculate initial prices
    calculateTotals();

    // Set up event listeners
    setupEventListeners();
    updateQuantitySavingsDisplay();
    refreshQuantityWelcomeKitUI();
    updateAddToCartButtonText();
    observeUpsellDialogModeChanges();

    // Listen for gift line changes and recalculate totals
    document.addEventListener('pbfcmpdp:gift-line-changed', () => {
      recalculateTotalsOnly();
    });

    applyLatchedOfferToBuyBox();

  }

  /**
   * Load configuration from hidden fields
   */
  function loadConfig() {
    const variantIdEl = document.getElementById(CONFIG.IDS.VARIANT_ID);
    const productIdEl = document.getElementById(CONFIG.IDS.PRODUCT_ID);
    const sellingPlanIdEl = document.getElementById(CONFIG.IDS.SELLING_PLAN_ID);
    const addonPlanIdEl = document.getElementById(CONFIG.IDS.ADDON_PLAN_ID);
    const basePriceEl = document.getElementById(CONFIG.IDS.BASE_PRICE);
    const subscriptionDiscountEl = document.getElementById(CONFIG.IDS.SUBSCRIPTION_DISCOUNT);
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    const bfcmModeEl = document.getElementById(CONFIG.IDS.BFCM_MODE);
    const bfcmDiscountEl = document.getElementById(CONFIG.IDS.BFCM_DISCOUNT_PERCENT);

    if (variantIdEl) state.variantId = parseInt(variantIdEl.value);
    if (productIdEl) state.productId = parseInt(productIdEl.value);
    if (sellingPlanIdEl) {
      state.sellingPlanId = sellingPlanIdEl.value;
      CONFIG.STAY_SELLING_PLAN_ID = sellingPlanIdEl.value;
    }
    if (addonPlanIdEl) {
      CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID = addonPlanIdEl.value || null;
    } else {
      console.warn('⚠️ [PBFCM-PDP] Hidden field PBFCM-addon-plan-id not found');
    }

    // Add warning if addon plan is not set
    if (!CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID) {
      console.warn('⚠️ [PBFCM-PDP] ADD_ON_SUBSCRIPTION_PLAN_ID is not configured - add-on subscriptions will not work correctly');
      console.warn('⚠️ [PBFCM-PDP] Please configure settings.addon_subscription_plan_id in Shopify admin');
    }

    // Load starter metafields
    const starterMultivitaminEl = document.getElementById('PBFCM-starter-multivitamin');
    const starterMagnesiumEl = document.getElementById('PBFCM-starter-magnesium');
    const starterBioticEl = document.getElementById('PBFCM-starter-biotic');

    CONFIG.STARTER_METAFIELDS = {
      multivitamin: starterMultivitaminEl ? starterMultivitaminEl.value : '',
      magnesium: starterMagnesiumEl ? starterMagnesiumEl.value : '',
      biotic: starterBioticEl ? starterBioticEl.value : ''
    };

    CONFIG.STARTER_PREVENT_SUBSCRIPTION = {
      multivitamin: document.getElementById('PBFCM-starter-multivitamin-prevent-subscription')?.value || '',
      magnesium: document.getElementById('PBFCM-starter-magnesium-prevent-subscription')?.value || '',
      biotic: document.getElementById('PBFCM-starter-biotic-prevent-subscription')?.value || '',
    };

    // Bundle metaobject variant ids
    const bundleCartDataEl = document.getElementById('PBFCM-bundle-cart-data');
    CONFIG.BUNDLE_CART_DATA = { source: 'none', handle: '', productHandle: '', variantIds: [] };
    CONFIG.BUNDLE_VARIANT_IDS = [];
    if (bundleCartDataEl) {
      try {
        const parsed = JSON.parse(bundleCartDataEl.textContent.trim());
        CONFIG.BUNDLE_CART_DATA = parsed;
        CONFIG.BUNDLE_VARIANT_IDS = Array.isArray(parsed.variantIds)
          ? parsed.variantIds.map((id) => String(id)).filter((id) => id && id !== '')
          : [];
        if (parsed.parentProduct && parsed.parentProduct.id) {
          window.__parentPdpCatalog = window.__parentPdpCatalog || {};
          window.__parentPdpCatalog[String(parsed.parentProduct.id)] = parsed.parentProduct;
        }
      } catch (error) {
        CONFIG.BUNDLE_CART_DATA = { source: 'none', handle: '', productHandle: '', variantIds: [] };
        CONFIG.BUNDLE_VARIANT_IDS = [];
      }
    }

    // Load bundle name for starter variants
    const bundleNameEl = document.getElementById('PBFCM-bundle-name');
    CONFIG.BUNDLE_NAME = bundleNameEl ? bundleNameEl.value : '';

    if (basePriceEl) state.prices.base = parseFloat(basePriceEl.value) / 100;
    if (subscriptionDiscountEl) CONFIG.SUBSCRIPTION_DISCOUNT = parseFloat(subscriptionDiscountEl.value);

    if (selectedQuantityEl) state.quantity = parseInt(selectedQuantityEl.value);
    if (bfcmModeEl) {
      const modeValue = bfcmModeEl.value;
      CONFIG.BFCM_MODE_ENABLED = modeValue === 'true' || modeValue === '1';
    }
    if (bfcmDiscountEl) {
      const percentValue = parseFloat(bfcmDiscountEl.value);
      if (!isNaN(percentValue)) {
        CONFIG.BFCM_DISCOUNT_PERCENT = percentValue;
        CONFIG.BFCM_DISCOUNT_MULTIPLIER = Math.max((100 - percentValue) / 100, 0);
      }
    }

    // Sub coupon % (FIREWORKS). Plan % is separate — never overwrite coupon with Stay plan %.
    CONFIG.SUB_PERCENTAGE_FINAL = false;
    CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT =
      document.getElementById(CONFIG.IDS.PLAN_SPECIFIC_SUB_DISCOUNT)?.value === 'true';

    const subDiscountPercentageEl = document.getElementById('PBFCM-sub-discount-percentage');
    if (subDiscountPercentageEl) {
      const pct = parseFloat(subDiscountPercentageEl.value);
      if (Number.isFinite(pct) && pct > 0 && pct <= 100) {
        const couponMult = (100 - pct) / 100;
        if (CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT) {
          CONFIG.SUBSCRIPTION_DISCOUNT = getPlanDiscountMultiplier() * couponMult;
        } else {
          CONFIG.SUBSCRIPTION_DISCOUNT = couponMult;
        }
        CONFIG.SUB_PERCENTAGE_FINAL = true;
      }
    }

    // Load straight-to-checkout configuration
    const straightToCheckoutEl = document.getElementById(CONFIG.IDS.STRAIGHT_TO_CHECKOUT);
    if (straightToCheckoutEl) {
      const stcValue = straightToCheckoutEl.value;
      CONFIG.STRAIGHT_TO_CHECKOUT = stcValue === 'true' || stcValue === '1' || stcValue === 'yes';
    } else {
      CONFIG.STRAIGHT_TO_CHECKOUT = false;
    }

  }

  /**
   * Overlay a latched funnel offer onto catalog PDP prices + ATC coupon state.
   * Funnel landing pages keep their own per-option coupons.
   */
  function applyLatchedOfferToBuyBox() {
    const offer = window.FDOffer && window.FDOffer.active;
    if (!offer || !offer.replacesGlobal) return;
    if (window.FDOffer.overridesCatalogPdp &&
        !window.FDOffer.overridesCatalogPdp(window.location && window.location.pathname)) {
      return;
    }

    const subEl = document.getElementById('PBFCM-sub-discount-percentage');
    if (subEl && Number.isFinite(offer.subPct)) subEl.value = String(offer.subPct);

    const otpEl = document.getElementById(CONFIG.IDS.BFCM_DISCOUNT_PERCENT);
    if (otpEl && Number.isFinite(offer.otpPct)) otpEl.value = String(offer.otpPct);

    if (Number.isFinite(offer.otpPct)) {
      CONFIG.BFCM_DISCOUNT_PERCENT = offer.otpPct;
      CONFIG.BFCM_DISCOUNT_MULTIPLIER = Math.max((100 - offer.otpPct) / 100, 0);
    }

    if (Number.isFinite(offer.subPct) && offer.subPct >= 0 && offer.subPct <= 100) {
      const couponMult = (100 - offer.subPct) / 100;
      CONFIG.SUBSCRIPTION_DISCOUNT = CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT
        ? getPlanDiscountMultiplier() * couponMult
        : couponMult;
      CONFIG.SUB_PERCENTAGE_FINAL = true;
    }

    const subCode = String(offer.subCode || offer.otpCode || '').trim();
    const otpCode = String(offer.otpCode || offer.subCode || '').trim();
    const isOtp = state.frequency === 'onetime';
    if (isOtp && otpCode) state.optionAtcCoupon = otpCode;
    else if (subCode) state.optionAtcCoupon = subCode;

    if (!selectedOptionIsExplicitZero()) {
      const displayPct = isOtp ? offer.otpPct : offer.subPct;
      state.optionDisplayDiscountPercent =
        (Number.isFinite(displayPct) && displayPct >= 0 && displayPct <= 100) ? displayPct : null;
    }

    const base = Number(state.prices.base) || 0;
    const monthlyPriceEl = document.getElementById('PBFCM-monthly-price');
    if (monthlyPriceEl && base > 0) {
      const formatted = formatMoney(getSubscriptionSaleUnit());
      monthlyPriceEl.innerHTML =
        `<span class="sr-only">Sale price ${formatted}</span><span aria-hidden="true">${formatted}</span>`;
    }
    const otpPriceEl = document.getElementById('PBFCM-otp-price');
    if (otpPriceEl && base > 0) {
      const formatted = formatMoney(base * CONFIG.BFCM_DISCOUNT_MULTIPLIER);
      otpPriceEl.innerHTML =
        `<span class="sr-only">Sale price ${formatted}</span><span aria-hidden="true">${formatted}</span>`;
      otpPriceEl.setAttribute('data-discount-percent', String(CONFIG.BFCM_DISCOUNT_PERCENT));
    }

    calculateTotals();
    updateUpsellPriceDisplays();
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    // Radio button change listeners
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    if (monthlyRadio) {
      monthlyRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('monthly');
      });
    }

    if (onetimeRadio) {
      onetimeRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('onetime');
      });
    }

    bindSubscriptionIntervalSelect();
  }

  let intervalChangeBound = false;
  function bindSubscriptionIntervalSelect() {
    // One delegated listener — works after theme-editor re-renders too
    if (intervalChangeBound) return;
    intervalChangeBound = true;
    document.addEventListener('change', function(e) {
      if (!e.target || e.target.id !== CONFIG.IDS.SUBSCRIPTION_INTERVAL) return;
      onSubscriptionIntervalChange(e.target);
    });
  }

  function onSubscriptionIntervalChange(selectEl) {
    if (!selectEl || selectEl.id !== CONFIG.IDS.SUBSCRIPTION_INTERVAL) return;
    const option = selectEl.options[selectEl.selectedIndex];
    if (!option) return;

    // Prevent inline onchange + delegated listener from double-applying
    const token = `${option.value}:${selectEl.selectedIndex}`;
    if (selectEl.dataset.pbfcmLastApply === token) return;
    selectEl.dataset.pbfcmLastApply = token;
    setTimeout(() => {
      if (selectEl.dataset.pbfcmLastApply === token) delete selectEl.dataset.pbfcmLastApply;
    }, 0);

    if (state.frequency !== 'monthly') {
      selectFrequency('monthly');
    }

    applySubscriptionPlanFromOption(option, { recalculate: true });
  }

  /**
   * Select purchase frequency (monthly subscription or one-time)
   * @param {string} frequency - 'monthly' or 'onetime'
   */
  function selectFrequency(frequency) {
    console.log('🔄 [PBFCM-PDP] Frequency changed to:', frequency);

    state.frequency = frequency;
    const isMonthly = frequency === 'monthly';

    // Update UI
    const monthlyOption = document.getElementById(CONFIG.IDS.FREQUENCY_MONTHLY);
    const onetimeOption = document.getElementById(CONFIG.IDS.FREQUENCY_ONETIME);
    const monthlyCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_MONTHLY);
    const onetimeCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_ONETIME);
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    // Get benefits and badge elements
    const monthlyBenefits = document.getElementById('PBFCM-monthly-benefits');
    const onetimeBenefits = document.getElementById('PBFCM-onetime-benefits');
    const onetimeBadge = document.getElementById('PBFCM-onetime-badge');

    if (isMonthly) {
      state.welcomeGiftIds = parseWelcomeGiftIds(monthlyOption?.dataset?.welcomeGiftIds);
      // Select monthly
      if (monthlyOption) {
        monthlyOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.remove('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '';
      }
      if (onetimeOption) {
        onetimeOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.remove('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'inline-flex';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'none';
      if (monthlyRadio) monthlyRadio.checked = true;
      if (onetimeRadio) onetimeRadio.checked = false;

      // Show monthly benefits, hide one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.add('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.add('PBFCM-PDP-frequency-selector__badge--hidden');

      // Restore selling plan from interval dropdown (auto-fetch)
      const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
      if (intervalSelect && intervalSelect.value) {
        applySubscriptionPlanFromOption(intervalSelect.options[intervalSelect.selectedIndex], { recalculate: false });
      }
    } else {
      state.welcomeGiftIds = [];
      // Select one-time
      if (monthlyOption) {
        monthlyOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.add('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '1px solid var(--color-helper-outline, #D7EFFA)';
      }
      if (onetimeOption) {
        onetimeOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.add('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'none';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'inline-flex';
      if (monthlyRadio) monthlyRadio.checked = false;
      if (onetimeRadio) onetimeRadio.checked = true;

      // Hide monthly benefits, show one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.add('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.remove('PBFCM-PDP-frequency-selector__badge--hidden');
    }

    // Update CSS variable based on selection
    const root = document.documentElement;
    if (frequency === 'onetime') {
      root.style.setProperty('--PBFCM-color-success', '#476ded');
    } else {
      root.style.setProperty('--PBFCM-color-success', '#078942');
    }

    // Note: the quantity tile's selected look (light-blue fill + default border)
    // and the savings-badge accent are driven entirely by the --selected CSS
    // classes, which are independent of the frequency swap above — so nothing
    // about the quantity tiles needs to be re-applied here on frequency change.

    // Recalculate totals and update upsell prices
    updateQuantitySavingsDisplay();
    refreshQuantityWelcomeKitUI();
    updateUpsellPriceDisplays();
    calculateTotals();
  }

  function getValidSellingPlanIds() {
    try {
      const data = JSON.parse(document.getElementById('PBFCM-variant-selling-plans-data')?.textContent || '{}');
      return (data.plans || []).map((p) => String(p.planId)).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function resolveSellingPlanIdForCart() {
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    const hiddenEl = document.getElementById(CONFIG.IDS.SELLING_PLAN_ID);
    // Prefer live dropdown value — never let a stale default win over the UI
    const fromInterval = intervalSelect && intervalSelect.value ? String(intervalSelect.value) : null;
    const fromHidden = hiddenEl && hiddenEl.value ? String(hiddenEl.value) : null;
    let planId = fromInterval || fromHidden || (state.sellingPlanId ? String(state.sellingPlanId) : null) || CONFIG.STAY_SELLING_PLAN_ID;
    const validIds = getValidSellingPlanIds().map(String);

    // Only fall back if the chosen id is truly unknown — keep selected option otherwise
    if (validIds.length > 0 && planId && !validIds.includes(String(planId))) {
      const selectedOpt = intervalSelect?.options?.[intervalSelect.selectedIndex];
      if (selectedOpt?.value) {
        planId = String(selectedOpt.value);
      } else {
        planId = validIds[0];
      }
    }

    if (planId) {
      if (hiddenEl) hiddenEl.value = planId;
      state.sellingPlanId = planId;
      CONFIG.STAY_SELLING_PLAN_ID = planId;
    }

    return planId || null;
  }

  /**
   * Plan to stamp on OTP lines for cart “Subscribe & Save” (_if_upgrade__selling_plan_id).
   * Prefer the selected frequency option’s Selling Plan ID (OTP box can carry one),
   * then a sibling subscription option matching the same variant, then any sub option,
   * then the normal cart plan resolver / theme stay.
   */
  function resolveUpgradeSellingPlanId(variantId = null) {
    const root = document.querySelector('.PBFCM-PDP-frequency-selector--configurable');
    const selected = getSelectedFrequencyOption();
    const selectedPlan = selected?.dataset?.sellingPlan?.trim();
    if (selectedPlan) return selectedPlan;

    if (root) {
      const targetVariant = variantId != null
        ? String(variantId)
        : (selected?.dataset?.variantId || (state.variantId != null ? String(state.variantId) : ''));
      const subOptions = Array.from(
        root.querySelectorAll('[data-frequency-id][data-option-kind="subscription"]')
      );
      const matchVariant = subOptions.find((opt) => {
        const plan = opt.dataset.sellingPlan?.trim();
        if (!plan) return false;
        const optVariant = opt.dataset.variantId || '';
        return targetVariant && optVariant && String(optVariant) === targetVariant;
      });
      if (matchVariant?.dataset?.sellingPlan) {
        return String(matchVariant.dataset.sellingPlan).trim();
      }
      const anySub = subOptions.find((opt) => opt.dataset.sellingPlan?.trim());
      if (anySub?.dataset?.sellingPlan) {
        return String(anySub.dataset.sellingPlan).trim();
      }
    }

    return resolveSellingPlanIdForCart() || CONFIG.STAY_SELLING_PLAN_ID || null;
  }

  function syncSubscriptionIntervalFromDom() {
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    if (!intervalSelect) {
      resolveSellingPlanIdForCart();
      return;
    }
    applySubscriptionPlanFromOption(intervalSelect.options[intervalSelect.selectedIndex], { recalculate: false });
  }

  function formatMoneyCents(cents) {
    const dollars = (Number(cents) || 0) / 100;
    const fixed = dollars.toFixed(2);
    if (fixed.endsWith('.00')) return `$${Math.round(dollars)}`;
    return `$${fixed}`;
  }

  function getPlanDiscountMultiplier() {
    const el = document.getElementById('PBFCM-plan-discount-percentage');
    const pct = parseFloat(el?.value);
    if (Number.isFinite(pct) && pct > 0 && pct <= 100) return (100 - pct) / 100;
    return 1;
  }

  function parseExplicitDiscountPercent(raw) {
    if (raw == null || raw === '') return null;
    const pct = parseFloat(raw);
    if (Number.isFinite(pct) && pct >= 0 && pct <= 100) return pct;
    return null;
  }

  function getOptionDisplayMultiplier() {
    const pct = state.optionDisplayDiscountPercent;
    if (Number.isFinite(pct) && pct >= 0 && pct <= 100) return (100 - pct) / 100;
    return null;
  }

  function getSubCouponMultiplier() {
    const optionMult = getOptionDisplayMultiplier();
    if (optionMult !== null) return optionMult;
    const el = document.getElementById('PBFCM-sub-discount-percentage');
    const pct = parseFloat(el?.value);
    if (Number.isFinite(pct) && pct > 0 && pct <= 100) return (100 - pct) / 100;
    return 1;
  }

  /** Compare unit for sub (matches cart: Stay allocation price, not MSRP). */
  function getSubscriptionCompareUnit() {
    if (CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT && Number.isFinite(CONFIG.PLAN_UNIT_PRICE) && CONFIG.PLAN_UNIT_PRICE > 0) {
      return CONFIG.PLAN_UNIT_PRICE;
    }
    return Number(state.prices.base) || 0;
  }

  /** Sale unit for sub = compare × coupon (FIREWORKS), matching cart. */
  function getSubscriptionSaleUnit() {
    const compareUnit = getSubscriptionCompareUnit();
    if (CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT) {
      return compareUnit * getSubCouponMultiplier();
    }
    return compareUnit * CONFIG.SUBSCRIPTION_DISCOUNT;
  }

  /** ATC / summary qty = quantity-selector × frequency block qty (e.g. 2 pouches × 3 for 90-day). */
  function getEffectiveQuantity() {
    const blockQty = Number(state.blockQty);
    const multiplier = Number.isFinite(blockQty) && blockQty > 0 ? blockQty : 1;
    return (Number(state.quantity) || 1) * multiplier;
  }

  function refreshPlanStackedSubscriptionDiscount() {
    if (!CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT) return;
    CONFIG.SUBSCRIPTION_DISCOUNT = getPlanDiscountMultiplier() * getSubCouponMultiplier();
    CONFIG.SUB_PERCENTAGE_FINAL = true;
  }

  function applySubscriptionPlanFromOption(option, opts = {}) {
    if (!option) return;

    const planId = option.value || '';
    const discountPctRaw = parseFloat(option.dataset.discountPercent);
    const discountPct = (Number.isFinite(discountPctRaw) && discountPctRaw > 0 && discountPctRaw <= 100)
      ? discountPctRaw
      : null;
    const allocationPriceCents = parseInt(option.dataset.allocationPrice, 10);

    const planInput = document.getElementById(CONFIG.IDS.SELLING_PLAN_ID);
    if (planInput) planInput.value = planId;
    state.sellingPlanId = planId || null;
    CONFIG.STAY_SELLING_PLAN_ID = planId || null;

    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    if (monthlyRadio) {
      const intervalDays = parseInt(option.dataset.intervalDays, 10);
      if (intervalDays !== 90 && !isNinetyDaySellingPlanId(planId)) {
        monthlyRadio.dataset.sellingPlan = planId;
      }
    }

    // Stay plan % only — keep coupon % (FIREWORKS) untouched for ATC stamp
    const planPctEl = document.getElementById('PBFCM-plan-discount-percentage');
    if (planPctEl && discountPct !== null) {
      planPctEl.value = String(discountPct);
    }

    if (Number.isFinite(allocationPriceCents) && allocationPriceCents > 0) {
      CONFIG.PLAN_UNIT_PRICE = allocationPriceCents / 100;
    } else if (discountPct !== null) {
      CONFIG.PLAN_UNIT_PRICE = (Number(state.prices.base) || 0) * ((100 - discountPct) / 100);
    }

    refreshPlanStackedSubscriptionDiscount();

    const couponMult = getSubCouponMultiplier();
    const compareUnit = getSubscriptionCompareUnit();

    const monthlyCompareEl = document.getElementById('PBFCM-monthly-compare');
    if (monthlyCompareEl && compareUnit > 0) {
      const formatted = formatMoneyCents(Math.round(compareUnit * 100));
      monthlyCompareEl.innerHTML =
        `<span class="sr-only">Original price ${formatted}, strikethrough</span>` +
        `<span aria-hidden="true">${formatted}</span>`;
    }

    const monthlyPriceEl = document.getElementById('PBFCM-monthly-price');
    if (monthlyPriceEl && compareUnit > 0) {
      const finalPrice = compareUnit * couponMult;
      const formatted = formatMoney(finalPrice);
      monthlyPriceEl.innerHTML =
        `<span class="sr-only">Sale price ${formatted}</span>` +
        `<span aria-hidden="true">${formatted}</span>`;
    }

    if (opts.recalculate !== false) {
      updateQuantitySavingsDisplay();
      updateUpsellPriceDisplays();
      calculateTotals();
    } else {
      syncEqualCompareAtVisibility();
    }
  }

  function parseWelcomeGiftIds(raw) {
    if (!raw || typeof raw !== 'string') return [];
    const seen = new Set();
    return raw
      .split(',')
      .map((s) => parseInt(String(s).trim(), 10))
      .filter((id) => {
        if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }

  function getQuantityWelcomeKitConfig() {
    const el = document.getElementById('PBFCM-quantity-welcome-kit-data');
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      if (!parsed || parsed.enabled !== true || !Array.isArray(parsed.gifts)) return null;
      return parsed;
    } catch (err) {
      console.warn('🎁 [PBFCM-PDP] Could not parse quantity welcome kit config', err);
      return null;
    }
  }

  function resolveQuantityGiftQuantity(gift, kidCount) {
    const qty = Math.max(parseInt(kidCount, 10) || 1, 1);
    const unlockQty = Math.max(parseInt(gift?.unlockQuantity, 10) || 1, 1);
    if (qty < unlockQty) return 0;
    const rule = String(gift?.quantityRule || 'fixed');
    if (rule === 'match_kids') return qty;
    if (rule === 'additional_kids') return Math.max(qty - 1, 0);
    return Math.max(parseInt(gift?.fixedQuantity, 10) || 1, 1);
  }

  function replaceQuantityKitTokens(template, data) {
    if (!template) return '';
    return String(template)
      .replaceAll('{quantity}', String(data.quantity))
      .replaceAll('{next_quantity}', String(data.nextQuantity))
      .replaceAll('{gift_count}', String(data.giftCount))
      .replaceAll('{gift_value}', data.giftValue);
  }

  function updateGiftFrequencyLock(card, locked, text, eligibility) {
    card.dataset.frequencyLocked = locked ? 'true' : 'false';
    const media = card.querySelector('.free-gifts-bar__media, .PBFCM-PDP-quantity-welcome-gift__image-wrap');
    let overlay = card.querySelector('[data-gift-lock-overlay]');
    if (!overlay && media) {
      overlay = document.createElement('span');
      overlay.className = 'PBFCM-gift-lock-overlay';
      overlay.setAttribute('data-gift-lock-overlay', '');
      media.appendChild(overlay);
    }
    if (overlay) overlay.hidden = !locked;
    let icon = card.querySelector('[data-gift-lock-icon]');
    if (!icon && media) {
      icon = document.createElement('span');
      icon.className = 'PBFCM-gift-lock-icon';
      icon.setAttribute('data-gift-lock-icon', '');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><path d="M12 14v3"/></svg>';
      overlay.appendChild(icon);
    }
    if (icon) icon.hidden = !locked;
    let message = card.querySelector('[data-gift-lock-message]');
    if (!message) {
      message = document.createElement('span');
      message.className = 'PBFCM-gift-lock-message';
      message.setAttribute('data-gift-lock-message', '');
      (overlay || card).appendChild(message);
    }
    message.textContent = String(text || '').trim() || `${eligibility}-Day Bonus`;
    message.hidden = !locked;
  }

  function refreshQuantityWelcomeKitUI() {
    const roots = Array.from(document.querySelectorAll('[data-quantity-welcome-kit="true"]'));
    if (!roots.length) return;
    const config = getQuantityWelcomeKitConfig();
    if (!config) return;

    const kidCount = Math.max(parseInt(state.quantity, 10) || 1, 1);
    const maxQuantity = Math.max(parseInt(config.maxQuantity, 10) || 4, 1);
    const nextQuantity = Math.min(kidCount + 1, maxQuantity);

    // Directly connect Quantity Welcome Gift rule blocks to the existing
    // metaobject/free-gifts-bar cards by resolved variant ID. Position remains
    // only as a backward-compatible fallback.
    const rulesByVariant = new Map();
    const rulesByPosition = new Map();
    (Array.isArray(config.gifts) ? config.gifts : []).forEach((rule) => {
      const variantId = parseInt(rule?.variantId, 10);
      if (Number.isFinite(variantId) && variantId > 0 && !rulesByVariant.has(variantId)) {
        rulesByVariant.set(variantId, rule || {});
      }
      const position = Math.max(parseInt(rule?.legacyGiftPosition, 10) || 1, 1);
      if (!rulesByPosition.has(position)) rulesByPosition.set(position, rule || {});
    });

    roots.forEach((root) => {
      let giftCount = 0;
      let giftValueDollars = 0;
      const rootDays = String(root.dataset.frequencyDays || '').trim().toLowerCase();

      root.querySelectorAll('[data-quantity]').forEach((item) => {
        const itemQty = parseInt(item.dataset.quantity, 10);
        const selected = itemQty === kidCount;
        item.classList.toggle(CONFIG.CLASSES.QUANTITY_SELECTED, selected);
        item.setAttribute('aria-checked', selected ? 'true' : 'false');
        item.setAttribute('tabindex', selected ? '0' : '-1');
      });

      // Original Quantity Welcome Gift cards.
      root.querySelectorAll('[data-quantity-gift-card]').forEach((card) => {
        const gift = {
          unlockQuantity: parseInt(card.dataset.unlockQuantity, 10) || 1,
          quantityRule: card.dataset.quantityRule || 'fixed',
          fixedQuantity: parseInt(card.dataset.fixedQuantity, 10) || 1
        };
        const displayQty = resolveQuantityGiftQuantity(gift, kidCount);
        const eligibility = String(card.dataset.frequencyEligibility || 'all').trim().toLowerCase();
        const frequencyEligible = eligibility === 'all' || eligibility === rootDays;
        const active = displayQty > 0 && frequencyEligible;
        card.hidden = displayQty <= 0;
        updateGiftFrequencyLock(card, !frequencyEligible, card.dataset.lockedGiftText, eligibility);
        card.dataset.active = active ? 'true' : 'false';
        const qtyBadge = card.querySelector('[data-gift-display-quantity]');
        if (qtyBadge) {
          qtyBadge.textContent = `x${displayQty}`;
          qtyBadge.hidden = !active;
        }
        if (active) {
          giftCount += displayQty;
          const unitValue = parseFloat(card.dataset.valueDollars || '0');
          if (Number.isFinite(unitValue) && unitValue > 0) giftValueDollars += unitValue * displayQty;
        }
      });

      // Existing metaobject/free-gifts-bar cards used by Alternate Quantity.
      // Include every visible gift cell, even display-only perks that do not resolve
      // to a Shopify variant ID (for example, Tracked Shipping). Variant ID matching
      // remains preferred when available; position is the fallback for display-only items.
      const legacyCells = Array.from(root.querySelectorAll('.AQK__legacy-gifts .free-gifts-bar__cell'));
      legacyCells.forEach((cell, index) => {
        const variantId = parseInt(cell.getAttribute('data-gift-variant-id'), 10);
        const position = index + 1;
        const variantRule = Number.isFinite(variantId) && variantId > 0
          ? rulesByVariant.get(variantId)
          : null;
        const positionRule = rulesByPosition.get(position) || null;
        const matchedRule = variantRule || positionRule;
        const rule = matchedRule || {
          unlockQuantity: 1,
          quantityRule: 'fixed',
          fixedQuantity: 1,
          frequencyEligibility: 'all',
          // Unmatched legacy/display-only perks should never invent a quantity badge.
          showQuantityBadge: false,
          valueDollars: 0
        };

        const eligibility = String(rule?.frequencyEligibility || 'all').trim().toLowerCase();
        const frequencyEligible = !eligibility || eligibility === 'all' || eligibility === rootDays;
        const quantity = resolveQuantityGiftQuantity(rule, kidCount);
        const displayQty = frequencyEligible ? quantity : 0;
        const active = displayQty > 0;

        cell.hidden = quantity <= 0;
        updateGiftFrequencyLock(cell, !frequencyEligible, rule.lockedGiftText, eligibility);
        cell.dataset.active = active ? 'true' : 'false';
        cell.dataset.quantityGiftRule = 'true';
        cell.dataset.displayQuantity = String(displayQty);

        let qtyBadge = cell.querySelector('[data-legacy-gift-display-quantity]');
        const showBadge = !!matchedRule && rule?.showQuantityBadge !== false;
        if (!qtyBadge && showBadge) {
          qtyBadge = document.createElement('span');
          qtyBadge.className = 'AQK__legacy-gift-qty';
          qtyBadge.setAttribute('data-legacy-gift-display-quantity', '');
          qtyBadge.setAttribute('aria-label', 'Gift quantity');
          cell.appendChild(qtyBadge);
        }
        if (qtyBadge) {
          qtyBadge.textContent = `x${displayQty}`;
          qtyBadge.hidden = !active || !showBadge;
        }

        if (active) {
          giftCount += displayQty;

          // Prefer the Quantity Welcome Gift rule value. If this is a display-only
          // metaobject (no resolvable Shopify variant/rule match), fall back to the
          // visible original-price text rendered by free-gifts-bar (for example "$5").
          let unitValue = parseFloat(rule?.valueDollars || '0');
          if (!(Number.isFinite(unitValue) && unitValue > 0) && !matchedRule) {
            const cellText = String(cell.textContent || '');
            const moneyMatches = Array.from(cellText.matchAll(/\$\s*([0-9]+(?:\.[0-9]{1,2})?)/g));
            if (moneyMatches.length) {
              const parsedVisibleValue = parseFloat(moneyMatches[moneyMatches.length - 1][1]);
              if (Number.isFinite(parsedVisibleValue) && parsedVisibleValue > 0) {
                unitValue = parsedVisibleValue;
              }
            }
          }

          if (Number.isFinite(unitValue) && unitValue > 0) giftValueDollars += unitValue * displayQty;
        }
      });

      root.querySelectorAll('[data-quantity-gift-group]').forEach((group) => {
        const hasVisible = Array.from(group.querySelectorAll('[data-quantity-gift-card]')).some((card) => !card.hidden);
        group.dataset.empty = hasVisible ? 'false' : 'true';
      });

      const giftValue = giftValueDollars > 0
        ? `$${Number.isInteger(giftValueDollars) ? giftValueDollars : giftValueDollars.toFixed(2)}`
        : '';
      const tokens = { quantity: kidCount, nextQuantity, giftCount, giftValue };
      root.querySelectorAll('[data-template]').forEach((el) => {
        el.textContent = replaceQuantityKitTokens(el.dataset.template || '', tokens);
      });

      const valueEl = root.querySelector('[data-quantity-kit-value]');
      if (valueEl) {
        valueEl.textContent = giftValue;
        valueEl.style.display = giftValue ? '' : 'none';
      }

      const nextTierEl = root.querySelector('[data-next-tier-text]');
      if (nextTierEl) nextTierEl.style.display = kidCount >= maxQuantity ? 'none' : '';
    });
  }

  function buildQuantityWelcomeKitGiftItems() {
    const config = getQuantityWelcomeKitConfig();
    if (!config) return [];
    if (config.subscriptionOnly === true && state.frequency !== 'monthly') return [];

    const selectedFrequency = getSelectedFrequencyOption();
    const selectedDays = String(selectedFrequency?.dataset?.days || '').trim();
    const kidCount = Math.max(parseInt(state.quantity, 10) || 1, 1);
    const giftItems = [];

    // NEW preferred mode: the old frequency/metaobject system decides WHICH gifts exist.
    // quantity_welcome_gift blocks only augment those gifts by ordinal position.
    if (String(config.source || '') === 'legacy_frequency') {
      const legacyGiftIds = getSelectedWelcomeGiftVariantIds();
      const rulesByVariant = new Map();
      const rulesByPosition = new Map();

      (Array.isArray(config.gifts) ? config.gifts : []).forEach((rule) => {
        const ruleVariantId = parseInt(rule?.variantId, 10);
        if (Number.isFinite(ruleVariantId) && ruleVariantId > 0 && !rulesByVariant.has(ruleVariantId)) {
          rulesByVariant.set(ruleVariantId, rule || {});
        }
        const position = Math.max(parseInt(rule?.legacyGiftPosition, 10) || 1, 1);
        // Position is retained only as a fallback for older configurations.
        if (!rulesByPosition.has(position)) rulesByPosition.set(position, rule || {});
      });

      legacyGiftIds.forEach((variantId, index) => {
        const position = index + 1;
        const rule = rulesByVariant.get(parseInt(variantId, 10)) || rulesByPosition.get(position) || {
          legacyGiftPosition: position,
          unlockQuantity: 1,
          quantityRule: 'fixed',
          fixedQuantity: 1,
          frequencyEligibility: 'all'
        };

        const eligibility = String(rule?.frequencyEligibility || 'all').trim().toLowerCase();
        if (eligibility !== 'all' && eligibility !== String(selectedDays).toLowerCase()) return;

        const quantity = resolveQuantityGiftQuantity(rule, kidCount);
        if (quantity <= 0) return;

        const item = createItemData({
          variantId: parseInt(variantId, 10),
          quantity,
          // Gifts remain strict OTP lines; the base product alone carries the subscription plan.
          isSubscription: false,
          isAddon: true,
          properties: {
            _welcome_kit_gift: 'true',
            _quantity_welcome_gift: 'true',
            _free_gift: 'true',
            _prevent_subscription: 'true',
            _gift_for_kids: String(kidCount),
            _gift_unlock_quantity: String(rule.unlockQuantity || 1),
            _gift_frequency: eligibility,
            _legacy_welcome_gift_position: String(position),
            _gift_parent_product_id: state.productId != null ? String(state.productId) : '',
            _gift_parent_variant_id: state.variantId != null ? String(state.variantId) : '',
            [CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE]: '0'
          }
        });

        if (item) {
          delete item.selling_plan;
          delete item.sellingPlan;
          if (item.properties && CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID) {
            delete item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID];
          }
          giftItems.push(item);
        }
      });

      console.log('🎁 [PBFCM-PDP] Legacy/metaobject welcome gifts with quantity rules queued:', {
        selectedDays,
        kidCount,
        legacyGiftIds,
        rulesByVariant: Array.from(rulesByVariant.entries()),
        rulesByPosition: Array.from(rulesByPosition.entries()),
        giftItems
      });
      return giftItems;
    }

    // Backward-compatible original quantity-gift mode.
    config.gifts.forEach((gift) => {
      const eligibility = String(gift?.frequencyEligibility || 'all').trim().toLowerCase();
      if (eligibility !== 'all' && eligibility !== String(selectedDays).toLowerCase()) return;

      const variantId = parseInt(gift?.variantId, 10);
      if (!Number.isFinite(variantId) || variantId <= 0) return;

      const quantity = resolveQuantityGiftQuantity(gift, kidCount);
      if (quantity <= 0) return;

      const item = createItemData({
        variantId,
        quantity,
        isSubscription: false,
        isAddon: true,
        properties: {
          _welcome_kit_gift: 'true',
          _quantity_welcome_gift: 'true',
          _free_gift: 'true',
          _prevent_subscription: 'true',
          _gift_for_kids: String(kidCount),
          _gift_unlock_quantity: String(gift.unlockQuantity || 1),
          _gift_frequency: eligibility,
          _gift_parent_product_id: state.productId != null ? String(state.productId) : '',
          _gift_parent_variant_id: state.variantId != null ? String(state.variantId) : '',
          [CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE]: '0'
        }
      });

      if (item) {
        delete item.selling_plan;
        delete item.sellingPlan;
        if (item.properties && CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID) {
          delete item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID];
        }
        giftItems.push(item);
      }
    });

    console.log('🎁 [PBFCM-PDP] Quantity welcome kit gifts queued:', {
      selectedDays,
      kidCount,
      giftItems
    });
    return giftItems;
  }

  function mergeWelcomeKitGiftItems(...groups) {
    const byVariant = new Map();
    groups.flat().filter(Boolean).forEach((item) => {
      const variantId = parseInt(item?.id ?? item?.variantId, 10);
      if (!Number.isFinite(variantId) || variantId <= 0) return;
      const qty = Math.max(parseInt(item.quantity, 10) || 1, 1);
      const existing = byVariant.get(variantId);
      if (!existing || qty > (parseInt(existing.quantity, 10) || 1)) {
        byVariant.set(variantId, { ...item, quantity: qty });
      }
    });
    return Array.from(byVariant.values());
  }

  function collectWelcomeGiftIdsFromDom(root) {
    if (!root) return [];
    const seen = new Set();
    const ids = [];
    root.querySelectorAll('[data-gift-variant-id]').forEach((el) => {
      const id = parseInt(el.getAttribute('data-gift-variant-id'), 10);
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids;
  }

  function getSelectedWelcomeGiftVariantIds() {
    const selected =
      document.querySelector(
        '.PBFCM-PDP-frequency-selector--configurable .PBFCM-PDP-frequency-selector__option--selected'
      ) ||
      document.querySelector('#PBFCM-frequency-monthly.PBFCM-PDP-frequency-selector__option--selected') ||
      document.getElementById('PBFCM-frequency-monthly');

    const fromAttr = parseWelcomeGiftIds(selected?.dataset?.welcomeGiftIds);
    const fromState = Array.isArray(state.welcomeGiftIds) ? state.welcomeGiftIds : [];
    const fromDom = collectWelcomeGiftIdsFromDom(selected);

    const seen = new Set();
    const merged = [];
    [...fromAttr, ...fromState, ...fromDom].forEach((id) => {
      const n = parseInt(id, 10);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) return;
      seen.add(n);
      merged.push(n);
    });
    return merged;
  }

  function buildWelcomeKitGiftItems() {
    const giftIds = getSelectedWelcomeGiftVariantIds();
    const selected =
      document.querySelector(
        '.PBFCM-PDP-frequency-selector--configurable .PBFCM-PDP-frequency-selector__option--selected'
      ) ||
      document.querySelector('#PBFCM-frequency-monthly.PBFCM-PDP-frequency-selector__option--selected') ||
      document.getElementById('PBFCM-frequency-monthly');

    if (!giftIds.length) {
      const kitVisible = !!selected?.querySelector(
        '.PBFCM-PDP-frequency-selector__welcome-gifts, .free-gifts-bar, [data-gift-variant-id]'
      );
      if (kitVisible) {
        console.warn('🎁 [PBFCM-PDP] Welcome kit visible but no variant IDs to add', {
          welcomeGiftIdsAttr: selected?.dataset?.welcomeGiftIds || '',
          stateIds: state.welcomeGiftIds,
          hint: 'Pick a published, in-stock gift product (e.g. refillable-bottle)'
        });
      } else {
        console.log('🎁 [PBFCM-PDP] No welcome kit gifts for selected option');
      }
      return [];
    }

    const giftItems = giftIds.map((variantId) =>
      createItemData({
        variantId,
        quantity: 1,
        isSubscription: false,
        isAddon: true,
        properties: {
          _welcome_kit_gift: 'true',
          _free_gift: 'true',
          [CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE]: '0'
        }
      })
    );

    console.log('🎁 [PBFCM-PDP] Welcome kit gifts queued:', giftIds);
    return giftItems;
  }

  async function addWelcomeKitGiftsSeparately(giftItems) {
    if (!giftItems || !giftItems.length) return;
    if (typeof window.addToCart !== 'function') return;

    try {
      const giftRes = await window.addToCart({ items: giftItems });
      if (!giftRes.ok) {
        let detail = {};
        try {
          detail = await giftRes.json();
        } catch (e) { /* ignore */ }
        console.warn('🎁 [PBFCM-PDP] Welcome kit gift add failed (main product already in cart):', {
          status: giftRes.status,
          description: detail.description || detail.message || detail,
          giftItems
        });
        return;
      }
      console.log('🎁 [PBFCM-PDP] Welcome kit gifts added');
    } catch (err) {
      console.warn('🎁 [PBFCM-PDP] Welcome kit gift add error (main product already in cart):', err);
    }
  }

  function selectFrequencyOption(el, opts) {
    if (!el) return;
    const options = opts || {};
    const suppressAutoAtc = options.suppressAutoAtc === true;

    const freqRoot =
      el.closest('.PBFCM-PDP-frequency-selector--configurable') ||
      document.querySelector('.PBFCM-PDP-frequency-selector--configurable');
    const scoped = freqRoot
      ? freqRoot.querySelectorAll('[data-frequency-id]')
      : [];
    const afterMount = document.getElementById('PBFCM-otp-after-atc-mount');
    const summaryMount = document.getElementById('PBFCM-otp-below-summary-mount');
    const relocated = [
      ...(afterMount ? afterMount.querySelectorAll('[data-frequency-id]') : []),
      ...(summaryMount ? summaryMount.querySelectorAll('[data-frequency-id]') : [])
    ];
    const allOptions = new Set([...scoped, ...relocated]);
    allOptions.forEach((opt) => {
      const selected = opt === el;
      opt.classList.toggle(CONFIG.CLASSES.OPTION_SELECTED, selected);
      opt.classList.toggle('PBFCM-PDP-frequency-selector__option--collapsed', !selected);
      const expandable = opt.querySelector('.PBFCM-PDP-frequency-selector__expandable');
      if (expandable) {
        expandable.classList.toggle('PBFCM-PDP-frequency-selector__expandable--hidden', !selected);
      }
      const radio = opt.querySelector('input[type="radio"]');
      if (radio) radio.checked = selected;
    });

    const kind = el.dataset.optionKind || el.dataset.frequency || 'subscription';
    const isOtp = kind === 'otp' || kind === 'onetime';
    state.frequency = isOtp ? 'onetime' : 'monthly';
    state.frequencyOptionId = el.dataset.frequencyId || null;
    state.triggerUpsellForOption = el.dataset.triggerUpsell === 'true';
    state.welcomeGiftIds = parseWelcomeGiftIds(el.dataset.welcomeGiftIds);

    // Block qty multiplier — drives effective ATC quantity and _qtyPerChange property
    const rawBlockQty = parseInt(el.dataset.blockQty, 10);
    state.blockQty = Number.isFinite(rawBlockQty) && rawBlockQty > 0 ? rawBlockQty : 1;

    // Per-option ATC coupon + FE display % (Stay FO = 0; code applies at checkout)
    state.optionAtcCoupon = (el.dataset.atcCoupon || '').trim();
    state.optionDisplayDiscountPercent = el.hasAttribute('data-display-discount-percent')
      ? (parseExplicitDiscountPercent(el.getAttribute('data-display-discount-percent')) ?? 0)
      : null;

    const planId = el.dataset.sellingPlan || '';
    const planInput = document.getElementById('PBFCM-selling-plan-id');
    if (!isOtp) {
      // Active subscription binding for ATC
      state.sellingPlanId = planId || null;
      if (planInput && planId) {
        planInput.value = planId;
        CONFIG.STAY_SELLING_PLAN_ID = planId;
      }
    }
    // OTP: do not clear planInput / STAY — keep last sub plan as upgrade fallback.
    // OTP block Selling Plan ID (if set) is read at ATC via resolveUpgradeSellingPlanId().

    const optVariantId = parseInt(el.dataset.variantId, 10);
    if (Number.isFinite(optVariantId) && optVariantId > 0) {
      state.variantId = optVariantId;
      const variantIdEl = document.getElementById(CONFIG.IDS.VARIANT_ID);
      if (variantIdEl) variantIdEl.value = String(optVariantId);
    }

    // Per-block Stay allocation price (configurable N cards)
    const unitPriceCents = parseInt(el.dataset.unitPrice, 10);
    if (!isOtp && Number.isFinite(unitPriceCents) && unitPriceCents > 0) {
      CONFIG.PLAN_SPECIFIC_SUB_DISCOUNT = true;
      CONFIG.PLAN_UNIT_PRICE = unitPriceCents / 100;
      const planPctEl = document.getElementById('PBFCM-plan-discount-percentage');
      if (planPctEl && state.prices.base > 0) {
        const planPct = Math.round((1 - CONFIG.PLAN_UNIT_PRICE / state.prices.base) * 100);
        if (planPct > 0 && planPct < 100) planPctEl.value = String(planPct);
      }
      refreshPlanStackedSubscriptionDiscount();
    } else if (isOtp) {
      CONFIG.PLAN_UNIT_PRICE = null;
    }

    document.documentElement.style.setProperty(
      '--PBFCM-color-success',
      isOtp ? '#476ded' : '#078942'
    );

    updateQuantitySavingsDisplay();
    refreshQuantityWelcomeKitUI();
    updateUpsellPriceDisplays();
    calculateTotals();

    // Underline / simple OTP: select then auto ATC
    if (!suppressAutoAtc && el.dataset.autoAtc === 'true' && isOtp) {
      setTimeout(() => {
        const atcBtn = document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN);
        if (atcBtn && !atcBtn.disabled) {
          atcBtn.click();
          return;
        }
        if (typeof handleAddToCart === 'function') {
          handleAddToCart();
        }
      }, 0);
    }
  }

  function relocateOtpOptions() {
    const mounts = {
      below_cta: document.getElementById('PBFCM-otp-after-atc-mount'),
      below_summary: document.getElementById('PBFCM-otp-below-summary-mount')
    };

    document
      .querySelectorAll(
        '.PBFCM-PDP-frequency-selector--configurable [data-otp-placement], #PBFCM-otp-after-atc-mount [data-otp-placement], #PBFCM-otp-below-summary-mount [data-otp-placement]'
      )
      .forEach((opt) => {
        const placement = opt.dataset.otpPlacement;
        const mount = mounts[placement];
        if (mount && opt.parentElement !== mount) {
          mount.appendChild(opt);
        }
      });
  }

  // Back-compat alias
  function relocateOtpAfterAtc() {
    relocateOtpOptions();
  }

  function getSelectedFrequencyOption() {
    return (
      document.querySelector(
        '.PBFCM-PDP-frequency-selector--configurable [data-frequency-id].PBFCM-PDP-frequency-selector__option--selected'
      ) ||
      document.querySelector(
        '#PBFCM-otp-after-atc-mount [data-frequency-id].PBFCM-PDP-frequency-selector__option--selected'
      ) ||
      document.querySelector(
        '#PBFCM-otp-below-summary-mount [data-frequency-id].PBFCM-PDP-frequency-selector__option--selected'
      )
    );
  }

  function selectedOptionTriggersUpsell() {
    const opt = getSelectedFrequencyOption();
    if (opt && opt.dataset.triggerUpsell === 'true') return true;
    return null;
  }

  /**
   * Select quantity
   * @param {number} qty - Rendered quantity to select
   */
  function selectQuantity(qty) {
    console.log('🔢 [PBFCM-PDP] Quantity changed to:', qty);

    state.quantity = qty;

    // Update hidden field
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    if (selectedQuantityEl) selectedQuantityEl.value = qty;

    // Update UI - remove selection from all quantity items (supports standard and alternate layouts)
    document.querySelectorAll('[data-component="PBFCM-PDP-quantity-selector"] [data-quantity]').forEach((item) => {
      item.classList.remove(CONFIG.CLASSES.QUANTITY_SELECTED);
      item.style.borderColor = '';
      item.setAttribute('aria-checked', 'false');
      item.setAttribute('tabindex', '-1');

      // Also update savings badge
      const savingsBadge = item.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
      if (savingsBadge) {
        savingsBadge.classList.remove(CONFIG.CLASSES.SAVINGS_SELECTED);
        savingsBadge.style.backgroundColor = '';
      }
    });

    // Add selection to chosen item
    const selectedItem = document.getElementById(`PBFCM-quantity-${qty}`);
    if (selectedItem) {
      selectedItem.classList.add(CONFIG.CLASSES.QUANTITY_SELECTED);
      selectedItem.setAttribute('aria-checked', 'true');
      selectedItem.setAttribute('tabindex', '0');

      // Also update savings badge
      const savingsBadge = selectedItem.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
      if (savingsBadge) {
        savingsBadge.classList.add(CONFIG.CLASSES.SAVINGS_SELECTED);
        savingsBadge.style.backgroundColor = 'var(--PBFCM-color-savings-badge)';
      }
    }

    refreshQuantityWelcomeKitUI();

    // Recalculate totals
    calculateTotals();
  }

  /**
   * Handle keyboard interaction for the quantity radiogroup.
   * @param {KeyboardEvent} event
   */
  function handleQuantityKeydown(event) {
    const selector = event.currentTarget.closest(
      '[data-component="PBFCM-PDP-quantity-selector"], .PBFCM-PDP-quantity-selector'
    );
    const items = selector
      ? Array.from(selector.querySelectorAll('[data-quantity]'))
      : [];
    const currentIndex = items.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectQuantity(Number(event.currentTarget.dataset.quantity));
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % items.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = items.length - 1;
    } else {
      return;
    }

    if (currentIndex < 0 || items.length === 0) return;

    event.preventDefault();
    const nextItem = items[nextIndex];
    selectQuantity(Number(nextItem.dataset.quantity));
    nextItem.focus();
  }

  function parseDisplayedMoneyCents(el) {
    if (!el) return null;
    const visible = el.querySelector('[aria-hidden="true"]');
    const text = (visible || el).textContent;
    if (text == null || text === '') return null;
    try {
      return parseMoneyToCents(String(text));
    } catch (e) {
      return null;
    }
  }

  function hideEqualCompareAtPair(compareEl, saleEl) {
    if (!compareEl) return;
    const compareCents = parseDisplayedMoneyCents(compareEl);
    const saleCents = parseDisplayedMoneyCents(saleEl);
    const show = compareCents != null && saleCents != null && compareCents > saleCents;
    compareEl.hidden = !show;
    if (show) compareEl.style.removeProperty('display');
    else compareEl.style.setProperty('display', 'none');
  }

  function selectedOptionIsExplicitZero() {
    const el = document.querySelector('.PBFCM-PDP-frequency-selector__option--selected');
    if (!el || !el.hasAttribute('data-display-discount-percent')) return false;
    const raw = el.getAttribute('data-display-discount-percent');
    const n = parseFloat(raw);
    return raw === '' || (Number.isFinite(n) && n === 0);
  }

  function syncEqualCompareAtVisibility() {
    document.querySelectorAll('.PBFCM-PDP-frequency-selector__price-compare').forEach((compareEl) => {
      const saleEl = compareEl.parentElement?.querySelector('.PBFCM-PDP-frequency-selector__price-final');
      hideEqualCompareAtPair(compareEl, saleEl);
    });
    document.querySelectorAll('.PBFCM-PDP-cart-summary__line-compare').forEach((compareEl) => {
      const saleEl = compareEl.parentElement?.querySelector('.PBFCM-PDP-cart-summary__line-final');
      hideEqualCompareAtPair(compareEl, saleEl);
    });
    document.querySelectorAll('.PBFCM-PDP-upsell-card').forEach((card) => {
      hideEqualCompareAtPair(
        card.querySelector('.PBFCM-PDP-upsell-card__price-compare'),
        card.querySelector('.PBFCM-PDP-upsell-card__price-final')
      );
    });
    document.querySelectorAll('.upsell-dialog__upsell-price-box').forEach((box) => {
      hideEqualCompareAtPair(
        box.querySelector('.upsell-dialog__upsell-price-compare'),
        box.querySelector('.upsell-dialog__upsell-price-final')
      );
    });
  }

  function parseSavingsAmount(text) {
    const match = String(text || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : 0;
  }

  function setQuantitySavingsBadge(element, savings, formattedText) {
    const badge = element.closest('.PBFCM-PDP-quantity-selector__savings-badge');
    const amount = Math.round((Number(savings) || 0) * 100) / 100;
    if (amount <= 0) {
      if (badge) {
        badge.hidden = true;
        badge.style.setProperty('display', 'none');
      }
      return;
    }
    element.textContent = formattedText || `Save $${formatSavingsMoney(amount)}`;
    if (badge) {
      badge.hidden = false;
      badge.style.removeProperty('display');
    }
  }

  function updateBreakdownSavingsBadge(savingsDollars) {
    const savingsText = document.getElementById('PBFCM-breakdown-savings');
    if (!savingsText) return;
    const badge = savingsText.closest('.PBFCM-PDP-cart-summary__savings-badge');
    const amount = Number(savingsDollars) || 0;
    savingsText.textContent = `You are saving a total of ${formatMoney(amount)}!`;
    if (badge) badge.hidden = amount <= 0;
  }

  function updateQuantitySavingsDisplay() {
    const isSubscription = state.frequency === 'monthly';
    const savingsElements = document.querySelectorAll('.PBFCM-PDP-quantity-selector__savings-text');
    const blockQty = Number(state.blockQty);
    const blockMultiplier = Number.isFinite(blockQty) && blockQty > 0 ? blockQty : 1;

    const base = Number(state.prices.base) || 0;
    let compareUnit = 0;
    let saleUnit = 0;
    if (isSubscription) {
      compareUnit = getSubscriptionCompareUnit();
      saleUnit = getSubscriptionSaleUnit();
    } else {
      compareUnit = base;
      const otpMult = getOptionDisplayMultiplier();
      saleUnit = otpMult !== null ? base * otpMult : base * getBfcmDiscountMultiplier();
    }

    savingsElements.forEach((element) => {
      const item = element.closest('[data-quantity]');
      const qty = parseInt(item?.dataset.quantity, 10) || 1;

      if (compareUnit > 0) {
        const savings = Math.max(0, (compareUnit - saleUnit) * qty * blockMultiplier);
        setQuantitySavingsBadge(element, savings, `Save $${formatSavingsMoney(savings)}`);
        return;
      }

      const nextText = isSubscription ? element.dataset.subscriptionText : element.dataset.onetimeText;
      if (nextText) setQuantitySavingsBadge(element, parseSavingsAmount(nextText), nextText);
    });
  }

  function formatSavingsMoney(amount) {
    const rounded = Math.round((Number(amount) || 0) * 100) / 100;
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(2);
  }

  /**
   * Update upsell product quantity
   * @param {string|number} productId - Product ID
   * @param {number} change - Change in quantity (+1 or -1)
   */
  function updateUpsellQuantity(productId, change) {
    console.log('🎁 [PBFCM-PDP] Updating upsell quantity:', productId, change);

    const currentQty = state.upsells[productId] || 0;
    let newQty = currentQty + change;

    // Get max quantity from data attribute
    const upsellCard = document.querySelector(`[data-product-id="${productId}"]`);
    const upsellData = upsellCard?.querySelector('.PBFCM-upsell-data');
    const maxQty = upsellData ? parseInt(upsellData.dataset.max) : 5;

    // Clamp between 0 and max
    newQty = Math.max(0, Math.min(newQty, maxQty));

    // Update state
    if (newQty === 0) {
      delete state.upsells[productId];
    } else {
      state.upsells[productId] = newQty;
    }

    // Update display
    const qtyDisplay = document.getElementById(`PBFCM-upsell-qty-${productId}`);
    if (qtyDisplay) qtyDisplay.textContent = newQty;

    // Update hidden field
    if (upsellData) upsellData.value = newQty;

    // Update minus button opacity
    const minusBtn = upsellCard?.querySelector('[data-action="decrease"]');
    if (minusBtn) {
      minusBtn.style.opacity = newQty === 0 ? '0.3' : '1';
    }

    console.log('🎁 [PBFCM-PDP] Upsell quantities:', state.upsells);

    // Recalculate totals
    calculateTotals();
  }

  function getUpsellPriceData(upsellData) {
    if (!upsellData) {
      return {
        priceCents: 0,
        comparePriceCents: 0,
        onetimePriceCents: 0,
        price: 0,
        comparePrice: 0,
        onetimePrice: 0
      };
    }

    const rawPriceCents = parseInt(upsellData.dataset.price, 10);
    const rawCompareCents = parseInt(upsellData.dataset.comparePrice || upsellData.dataset.price, 10);
    const rawOnetimeCents = parseInt(upsellData.dataset.onetimePrice, 10);
    const priceCents = Number.isFinite(rawPriceCents) ? rawPriceCents : 0;
    const comparePriceCents = Number.isFinite(rawCompareCents) ? rawCompareCents : priceCents;
    const onetimePriceCents = Number.isFinite(rawOnetimeCents) ? rawOnetimeCents : priceCents;

    return {
      priceCents,
      comparePriceCents,
      onetimePriceCents,
      price: priceCents / 100,
      comparePrice: comparePriceCents / 100,
      onetimePrice: onetimePriceCents / 100
    };
  }

  /**
   * Parse money string to cents
   * @param {string|number} value - Money value (e.g., "$12.34" or "12.34" or 12.34)
   * @returns {number|null} - Value in cents or null if invalid
   */
  function parseMoneyToCents(value) {
      if(value.split("$") && value.split("$")[1]) {
          value = value.split("$")[1]
      }

    if (value === undefined || value === null) return null;
    const textValue = value.toString();
    if (textValue.toUpperCase().includes('FREE')) return 0;
    const normalized = textValue.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (!normalized) return null;
    const floatValue = parseFloat(normalized);
    if (isNaN(floatValue)) return null;

      return Math.round(floatValue * 100);
  }

  /**
   * Read the currently rendered PDP upsell prices from the DOM.
   * This keeps the dialog aligned with whatever price the shopper is seeing inline.
   * @param {string|number} productId - Upsell product ID
   * @returns {{ finalPriceCents: number|null, comparePriceCents: number|null }|null}
   */
  function getLiveUpsellPriceState(productId) {
    const upsellData = document.querySelector(`.PBFCM-upsell-data[data-upsell-id="${productId}"]`);
    const upsellCard = upsellData?.closest('.PBFCM-PDP-upsell-card') ||
      document.querySelector(`.PBFCM-PDP-upsell-card[data-product-id="${productId}"]`);

    if (!upsellData && !upsellCard) return null;

    const finalPriceText = upsellCard?.querySelector('.PBFCM-PDP-upsell-card__price-final [aria-hidden="true"]')?.textContent;
    const comparePriceText = upsellCard?.querySelector('.PBFCM-PDP-upsell-card__price-compare [aria-hidden="true"]')?.textContent;

    const datasetComparePrice = upsellData ? parseInt(upsellData.dataset.comparePrice || '', 10) : NaN;
    const otpSelected = state.frequency === 'onetime';
    const datasetSalePrice = upsellData
      ? parseInt((otpSelected ? upsellData.dataset.onetimePrice : upsellData.dataset.price) || '', 10)
      : NaN;
    const finalPriceCents = parseMoneyToCents(finalPriceText);
    const comparePriceCents = parseMoneyToCents(comparePriceText);

    return {
      finalPriceCents: Number.isFinite(finalPriceCents)
        ? finalPriceCents
        : (Number.isFinite(datasetSalePrice) ? datasetSalePrice : null),
      comparePriceCents: comparePriceCents ?? (Number.isFinite(datasetComparePrice) ? datasetComparePrice : null)
    };
  }

  /**
   * Sync dialog upsell product prices with the inline PDP cards.
   * @param {Array} upsellProducts - Dialog upsell products
   * @param {boolean} isSubscription - Whether monthly pricing is active
   * @returns {Array}
   */
  function syncDialogUpsellProductsWithLivePdp(upsellProducts, isSubscription) {
    return upsellProducts.map(product => {
      const livePriceState = getLiveUpsellPriceState(product.id);
      if (!livePriceState) return product;

      const syncedProduct = { ...product };

      if (Number.isFinite(livePriceState.comparePriceCents)) {
        syncedProduct.comparePrice = livePriceState.comparePriceCents;
      }

      if (Number.isFinite(livePriceState.finalPriceCents)) {
        syncedProduct.price = livePriceState.finalPriceCents;

        if (isSubscription) {
          syncedProduct.subscriptionPrice = livePriceState.finalPriceCents;
        } else {
          syncedProduct.onetimePrice = livePriceState.finalPriceCents;
        }
      }

      return syncedProduct;
    });
  }

  /**
   * Get total addon subtotal in cents
   * @returns {number} - Addon subtotal in cents
   */
  function getAddonSubtotalCents() {
    let addonTotal = 0;
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty > 0) {
        const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
        if (upsellData && upsellData.dataset.isAddon === 'true') {
          const priceData = getUpsellPriceData(upsellData);
          addonTotal += Math.round(priceData.price * qty * 100);
        }
      }
    });
    return addonTotal;
  }

  /**
   * Same free-shipping rule as the price summary shipping line:
   *   subscription → always free
   *   OTP → free when sale total >= CONFIG.FREE_SHIPPING_THRESHOLD ($50 today)
   */
  function qualifiesForFreeShipping({ isSubscription, totalDollars }) {
    return Boolean(isSubscription) || Number(totalDollars) >= CONFIG.FREE_SHIPPING_THRESHOLD;
  }

  /**
   * Dispatch summary updated event for gift progress component
   * @param {number} totalSaleCents - Total sale price in cents
   * @param {boolean} isFreeShipping - Whether shipping is free
   */
  function dispatchSummaryUpdatedEvent(totalSaleCents, isFreeShipping) {
    document.dispatchEvent(new CustomEvent('pbfcmpdp:summary-updated', {
      detail: {
        allItemsSubtotalCents: totalSaleCents,
        addonSubtotalCents: getAddonSubtotalCents(),
        shippingCents: isFreeShipping ? 0 : CONFIG.SHIPPING_COST * 100
      }
    }));
  }

  /**
   * Calculate all prices and update displays
   * Simplified to read from DOM elements with name attributes
   */
  function calculateTotals() {

    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;

    // Subscription sale unit matches cart (plan allocation × coupon)
    state.prices.subscription = isSubscription ? getSubscriptionSaleUnit() : basePrice * CONFIG.SUBSCRIPTION_DISCOUNT;
    // OTP: bake option display % into unit when set; otherwise leave base and stack BFCM in updatePriceDisplays
    const otpDisplayMult = getOptionDisplayMultiplier();
    state.prices.onetime = otpDisplayMult !== null ? basePrice * otpDisplayMult : basePrice;

    // First, ensure all individual items are rendered
    updatePriceDisplays();

    // Wait a tick for DOM to update
    requestAnimationFrame(() => {
      // The gift line carries name="cart-summary-compare-price" on the strikethrough
      // and no matching sale-price counterpart, so its value always shows up as pure
      // savings. Only let it contribute when the gift is actually unlocked
      // (data-active="true" set by PBFCM-PDP-gift-progress.liquid setGiftLine).
      const giftLineEl = document.getElementById('PBFCM-breakdown-gift-line');
      const giftActive = giftLineEl?.dataset.active === 'true';
      const isInactiveGiftLine = (el) =>
        !giftActive && giftLineEl && giftLineEl.contains(el);

      // Sum all compare prices (original prices)
      const compareElements = document.querySelectorAll('[name="cart-summary-compare-price"]');
      let totalCompare = 0;
      compareElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);

          if (cents !== null) totalCompare += cents;
      });

      // Sum all sale prices (discounted prices)
      const saleElements = document.querySelectorAll('[name="cart-summary-sale-price"]');
      let totalSale = 0;
      saleElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalSale += cents;
      });

      // Update subtotal displays
      const subtotalCompare = document.getElementById('PBFCM-breakdown-subtotal-compare');
      const subtotalFinal = document.getElementById('PBFCM-breakdown-subtotal-final');
      if (subtotalCompare) {
        const formattedCompare = formatMoney(totalCompare / 100);
        subtotalCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
      }
      if (subtotalFinal) {
        const formattedSale = formatMoney(totalSale / 100);
        subtotalFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
      }

      // Update state
      state.prices.total = totalSale / 100;
      state.prices.savings = (totalCompare - totalSale) / 100;

      // Update shipping display - free for subscriptions OR orders over threshold
      const shippingCompare = document.getElementById('PBFCM-breakdown-shipping-compare');
      const shippingFinal = document.getElementById('PBFCM-breakdown-shipping-final');
      const isFreeShipping = qualifiesForFreeShipping({
        isSubscription: isSubscription,
        totalDollars: state.prices.total
      });

      if (isFreeShipping) {
        // Show comparison: crossed-out $5.00 → FREE
        if (shippingCompare) {
          shippingCompare.style.display = '';
          const shippingCost = `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
          shippingCompare.innerHTML = `<span class="sr-only">Original shipping price ${shippingCost}, strikethrough</span><span aria-hidden="true">${shippingCost}</span>`;
        }
        if (shippingFinal) {
          shippingFinal.innerHTML = `<span class="sr-only">Free shipping</span><span aria-hidden="true">FREE</span>`;
        }
      } else {
        // Just show the price, no comparison
        if (shippingCompare) shippingCompare.style.display = 'none';
        if (shippingFinal) {
          const shippingCost = `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
          shippingFinal.innerHTML = `<span class="sr-only">Shipping price ${shippingCost}</span><span aria-hidden="true">${shippingCost}</span>`;
        }
      }

      updateBreakdownSavingsBadge(state.prices.savings);
      syncEqualCompareAtVisibility();

      // Mirror into any standalone Savings Display blocks
      updateSavingsDisplayBlocks(totalCompare, totalSale);

      // Dispatch event for gift progress
      dispatchSummaryUpdatedEvent(totalSale, isFreeShipping);

    });
  }

  /**
   * Recalculate totals only (without dispatching summary-updated event)
   * Used when gift line changes to avoid infinite loops
   */
  function recalculateTotalsOnly() {
    requestAnimationFrame(() => {
      // Skip gift-line contributions when the gift line isn't actually unlocked —
      // see comment in calculateTotals for why this matters.
      const giftLineEl = document.getElementById('PBFCM-breakdown-gift-line');
      const giftActive = giftLineEl?.dataset.active === 'true';
      const isInactiveGiftLine = (el) =>
        !giftActive && giftLineEl && giftLineEl.contains(el);

      const compareElements = document.querySelectorAll('[name="cart-summary-compare-price"]');
      let totalCompare = 0;
      compareElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalCompare += cents;
      });

      const saleElements = document.querySelectorAll('[name="cart-summary-sale-price"]');
      let totalSale = 0;
      saleElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalSale += cents;
      });

      // Update subtotal displays
      const subtotalCompare = document.getElementById('PBFCM-breakdown-subtotal-compare');
      const subtotalFinal = document.getElementById('PBFCM-breakdown-subtotal-final');
      if (subtotalCompare) {
        const formattedCompare = formatMoney(totalCompare / 100);
        subtotalCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
      }
      if (subtotalFinal) {
        const formattedSale = formatMoney(totalSale / 100);
        subtotalFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
      }

      // Update state
      state.prices.total = totalSale / 100;
      state.prices.savings = (totalCompare - totalSale) / 100;

      updateBreakdownSavingsBadge(state.prices.savings);
      syncEqualCompareAtVisibility();

      // Mirror into any standalone Savings Display blocks
      updateSavingsDisplayBlocks(totalCompare, totalSale);

      console.log('💰 [PBFCM-PDP] Totals recalculated after gift line change:', {
        totalCompare: totalCompare / 100,
        totalSale: totalSale / 100
      });
    });
  }

  /**
   * Update all price displays in the UI
   */
  function updatePriceDisplays() {
    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;
    const unitPrice = isSubscription ? state.prices.subscription : state.prices.onetime;
    const effectiveQty = getEffectiveQuantity();
    const mainProductTotal = unitPrice * effectiveQty;
    // Cart compare = Stay allocation × qty (not full MSRP) when auto-fetch plans are on
    const compareUnit = isSubscription ? getSubscriptionCompareUnit() : basePrice;
    const mainProductOriginalTotal = compareUnit * effectiveQty;

    // Update breakdown product line
    const productLabel = document.getElementById('PBFCM-breakdown-product-label');
    const productCompare = document.getElementById('PBFCM-breakdown-product-compare');
    const productFinal = document.getElementById('PBFCM-breakdown-product-final');

    // Get product title from page or default
    const productTitle = document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() || "Kids' Multivitamin";

    // Label + price both use effectiveQty (qty selector × block_qty)
    if (productLabel) productLabel.textContent = `${productTitle} (${effectiveQty}x)`;
    if (productCompare) {
      const formattedCompare = formatMoney(mainProductOriginalTotal);
      productCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
    }
    if (productFinal) {
      // Match frequency selector behavior: when sub_discount_percentage is set,
      // the SUBSCRIPTION_DISCOUNT already represents the FINAL combined discount —
      // BFCM must not stack again on the subscription line. OTP keeps the stack
      // unless a per-option display % already baked the sale into unit price.
      const hasOptionDisplayPct = getOptionDisplayMultiplier() !== null;
      const saleAmount = (isSubscription && CONFIG.SUB_PERCENTAGE_FINAL) || hasOptionDisplayPct
        ? mainProductTotal
        : applyBfcmDiscount(mainProductTotal);
      const formattedSale = formatMoney(saleAmount);
      productFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
    }

    // Update upsell lines in breakdown
    renderUpsellLines();
    updateUnderlineOtpPrices();
    syncEqualCompareAtVisibility();
  }

  /**
   * Underline OTP ("Or Try One-Time $X") is Liquid-rendered as unit sale price.
   * Multiply by the quantity-selector value so it matches ATC / cart summary.
   * Appends otp_shipping_charge_text only when that OTP total would NOT get free
   * shipping — same threshold as the summary (CONFIG.FREE_SHIPPING_THRESHOLD).
   */
  function updateUnderlineOtpPrices() {
    const qty = Number(state.quantity) || 1;
    const options = document.querySelectorAll(
      '.PBFCM-PDP-frequency-selector__option--underline-simple[data-unit-sale-price], #PBFCM-otp-after-atc-mount .PBFCM-PDP-frequency-selector__option--underline-simple[data-unit-sale-price], #PBFCM-otp-below-summary-mount .PBFCM-PDP-frequency-selector__option--underline-simple[data-unit-sale-price]'
    );
    options.forEach((opt) => {
      const unitCents = parseInt(opt.dataset.unitSalePrice, 10);
      if (!Number.isFinite(unitCents)) return;
      const label = (opt.dataset.optLabel || 'One-Time').trim();
      const el = opt.querySelector('.PBFCM-PDP-frequency-selector__underline-combined');
      if (!el) return;
      const total = (unitCents / 100) * qty;
      const shipText = (opt.getAttribute('data-shipping-charge-text') || '').trim();
      const otpGetsFreeShipping = qualifiesForFreeShipping({
        isSubscription: false,
        totalDollars: total
      });
      const suffix = shipText && !otpGetsFreeShipping ? ` ${shipText}` : '';
      el.textContent = `${label} ${formatMoney(total)}${suffix}`;
    });
  }

  /**
   * Render upsell lines in the cart summary breakdown
   */
  function renderUpsellLines() {
    const container = document.getElementById('PBFCM-breakdown-upsells-container');
    if (!container) return;

    // Clear existing upsell lines
    container.innerHTML = '';
    container.style.display = 'none';

    // Check if there are any upsells
    const hasUpsells = Object.keys(state.upsells).some(productId => state.upsells[productId] > 0);
    if (!hasUpsells) return;

    // Get success color from the page
    const successColor = getComputedStyle(document.documentElement).getPropertyValue('--success-color') || '#078942';

    // Render each upsell
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty === 0) return;

      const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
      console.log("[renderUpsellLines] upsell data", upsellData.dataset);
      if (!upsellData) return;

      const upsellTitle = upsellData.dataset.title || 'Upsell Product';
      const priceData = getUpsellPriceData(upsellData);
      const isAddOn = upsellData.dataset.isAddon === 'true';
      const isSubscription = state.frequency === 'monthly';
      const unitPrice = isSubscription ? priceData.price : priceData.onetimePrice;
      const originalPrice = priceData.comparePrice;

      const totalPrice = unitPrice * qty;
      const totalOriginalPrice = originalPrice * qty;
      const discountedTotalPrice = totalPrice;
      console.log("[renderUpsellLines], isSubscription", isSubscription);
      console.log("[renderUpsellLines], unitPrice", unitPrice);
      console.log("[renderUpsellLines], totalPrice", totalPrice);
      console.log("[renderUpsellLines], discountedTotalPrice", discountedTotalPrice);
      console.log("[renderUpsellLines], totalOriginalPrice", totalOriginalPrice);

      // Create upsell line HTML
      const upsellLineHTML = `
        <div class="PBFCM-PDP-cart-summary__line">
          <span class="PBFCM-PDP-cart-summary__line-label PBFCM-PDP-cart-summary__line-label--product">
            ${upsellTitle} (${qty}x)
          </span>
          <div class="PBFCM-PDP-cart-summary__line-price">
            <span class="PBFCM-PDP-cart-summary__line-compare" name="cart-summary-compare-price">
              <span class="sr-only">Original price ${formatMoney(totalOriginalPrice)}, strikethrough</span>
              <span aria-hidden="true">${formatMoney(totalOriginalPrice)}</span>
            </span>
            <span class="PBFCM-PDP-cart-summary__line-final" style="color: ${successColor};" name="cart-summary-sale-price">
              <span class="sr-only">Sale price ${formatMoney(discountedTotalPrice)}</span>
              <span aria-hidden="true">${formatMoney(discountedTotalPrice)}</span>
            </span>
          </div>
        </div>
      `;

      container.insertAdjacentHTML('beforeend', upsellLineHTML);
    });

    container.style.display = '';
  }

  /**
   * Update upsell card price displays based on current frequency
   * Called when frequency changes to show correct discount (subscription vs one-time)
   */
  function updateUpsellPriceDisplays() {
    const isSubscription = state.frequency === 'monthly';

    document.querySelectorAll('.PBFCM-PDP-upsell-card').forEach(card => {
      const upsellData = card.querySelector('.PBFCM-upsell-data');
      if (!upsellData) return;

      const priceData = getUpsellPriceData(upsellData);
      const displayPrice = isSubscription ? priceData.price : priceData.onetimePrice;

      const priceEl = card.querySelector('.PBFCM-PDP-upsell-card__price-final');
      if (priceEl) {
        const formattedPrice = formatMoney(displayPrice);
        priceEl.innerHTML = `<span class="sr-only">Sale price ${formattedPrice}</span><span aria-hidden="true">${formattedPrice}</span>`;
      }
    });

    syncEqualCompareAtVisibility();
  }

  function getBfcmDiscountMultiplier() {
    if (!CONFIG.BFCM_MODE_ENABLED) return 1;
    if (typeof CONFIG.BFCM_DISCOUNT_MULTIPLIER !== 'number' || CONFIG.BFCM_DISCOUNT_MULTIPLIER <= 0) {
      return 1;
    }
    return CONFIG.BFCM_DISCOUNT_MULTIPLIER;
  }

  function applyBfcmDiscount(amount) {
    if (typeof amount !== 'number') return amount;
    const multiplier = getBfcmDiscountMultiplier();
    return amount * multiplier;
  }

  /**
   * Format price as money string
   * @param {number} amount - Amount in dollars
   * @returns {string} Formatted money string
   */
  function formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Mirror the cart summary's subtotal + savings into any standalone
   * "Savings Display" blocks on the page (snippets/savings-display.liquid).
   * Fed the same totalCompare / totalSale the summary uses, so the block tracks
   * quantity, one-time vs subscription, upsell and gift changes in lockstep.
   * @param {number} totalCompareCents - Summed original price in cents
   * @param {number} totalSaleCents - Summed discounted price in cents
   */
  function updateSavingsDisplayBlocks(totalCompareCents, totalSaleCents) {
    const blocks = document.querySelectorAll('.fd-savings-display');
    if (!blocks.length) return;

    const savings = (totalCompareCents - totalSaleCents) / 100;
    const hasSavings = savings > 0;
    const formattedCompare = formatMoney(totalCompareCents / 100);
    const formattedSale = formatMoney(totalSaleCents / 100);

    blocks.forEach(block => {
      const compareValue = block.querySelector('[data-savings-compare]');
      const compareWrap = block.querySelector('.fd-savings-display__compare');
      const finalValue = block.querySelector('[data-savings-final]');
      const banner = block.querySelector('[data-savings-banner]');

      if (compareValue) compareValue.textContent = formattedCompare;
      if (finalValue) finalValue.textContent = formattedSale;
      if (compareWrap) compareWrap.hidden = !hasSavings;
      if (banner) {
        const template = block.dataset.savingsTemplate || 'You are saving a total of {amount}!';
        banner.textContent = template.replace('{amount}', formatMoney(savings));
        banner.hidden = !hasSavings;
      }
    });
  }

  /**
   * Copy coupon code to clipboard
   */
  function copyCouponCode() {
    const couponCodeEl = document.getElementById(CONFIG.IDS.COUPON_CODE);
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);

    if (!couponCodeEl) return;

    const code = couponCodeEl.textContent;

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => {
          console.log('📋 [PBFCM-PDP] Coupon code copied:', code);
          showCopyMessage();
        })
        .catch(err => {
          console.error('❌ [PBFCM-PDP] Failed to copy:', err);
          fallbackCopy(code);
        });
    } else {
      fallbackCopy(code);
    }
  }

  /**
   * Fallback copy method for older browsers
   * @param {string} text - Text to copy
   */
  function fallbackCopy(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      console.log('📋 [PBFCM-PDP] Coupon code copied (fallback):', text);
      showCopyMessage();
    } catch (err) {
      console.error('❌ [PBFCM-PDP] Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  /**
   * Show copy success message
   */
  function showCopyMessage() {
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);
    if (!copyMessageEl) return;

    // Show toast with animation
    copyMessageEl.classList.add('is-visible');

    // Hide after 2 seconds
    setTimeout(() => {
      copyMessageEl.classList.remove('is-visible');
    }, 2000);
  }

  /**
   * Create item data structure for cart
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  function uniqueBundleId() {
    return 'bundle-' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function shouldPreventSubscription(variantId) {
    if (!variantId) return false;
    console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] checking variantId:', variantId);
    const fields = ['multivitamin', 'magnesium', 'biotic'];
    for (const key of fields) {
      const starterVariant = CONFIG.STARTER_METAFIELDS[key];
      if (!starterVariant) continue;
      if (String(starterVariant) === String(variantId)) {
        const preventFlag = CONFIG.STARTER_PREVENT_SUBSCRIPTION[key];
        console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] matched field:', key, '| preventFlag:', preventFlag);
        if (preventFlag) return true;
      }
    }
    console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] no prevent flag matched');
    return false;
  }

  function isExcludedPlanUpgradeLine(props, isAddon) {
    if (!props || isAddon) return true;
    if (props[CONFIG.PROPERTIES.UPSELL_ITEM] === 'true' || props[CONFIG.PROPERTIES.UPSELL_ITEM] === true) return true;
    if (props._bundle_gift || props._box_picker_gift || props._welcome_kit_gift) return true;
    if (String(props._upsell_module || '').toLowerCase() === 'build_box') return true;
    if (props._quantity_welcome_gift || props._free_gift || props._free_unlock) return true;
    return false;
  }

  function looksLikeNinetyDayPlanName(name) {
    return /90|quarter|3 month|every 3|12 week/.test(String(name || '').toLowerCase());
  }

  function looksLikeThirtyDayPlanName(name) {
    const probe = String(name || '').toLowerCase();
    if (looksLikeNinetyDayPlanName(probe)) return false;
    if (/60|bimonth|bi-month|2 month|every 2|8 week/.test(probe)) return false;
    return /30|1 month|month|4 week/.test(probe);
  }

  function collectNinetyDaySellingPlanIds() {
    const ids = new Set();
    const thirtyIds = new Set();
    document.querySelectorAll('[data-frequency-id][data-option-kind="subscription"]').forEach((el) => {
      const id = (el.dataset.sellingPlan || '').trim();
      if (!id) return;
      const days = parseInt(el.dataset.days, 10);
      const period = String(el.dataset.frequencyPeriod || '');
      if (period === 'quarterly' || days === 90) return;
      if (days === 30 || period === 'monthly') thirtyIds.add(id);
    });
    document.querySelectorAll('[data-plan-upgrade-to]').forEach((el) => {
      const id = (el.dataset.planUpgradeTo || '').trim();
      if (id) ids.add(id);
    });
    document.querySelectorAll('[data-frequency-period="quarterly"], [data-days="90"]').forEach((el) => {
      const id = (el.dataset.sellingPlan || '').trim();
      if (id && !thirtyIds.has(id)) ids.add(id);
    });
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    Array.from((intervalSelect && intervalSelect.options) || []).forEach((opt) => {
      if (!opt.value) return;
      const days = parseInt(opt.dataset.intervalDays, 10);
      const name = opt.dataset.planName || opt.textContent || '';
      if (days === 90 || looksLikeNinetyDayPlanName(name)) ids.add(String(opt.value));
    });
    return ids;
  }

  function isNinetyDaySellingPlanId(planId) {
    const id = String(planId || '').trim();
    if (!id) return false;
    if (collectNinetyDaySellingPlanIds().has(id)) return true;
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    if (!intervalSelect) return false;
    const opt = Array.from(intervalSelect.options || []).find((o) => String(o.value) === id);
    if (!opt) return false;
    const days = parseInt(opt.dataset.intervalDays, 10);
    return days === 90 || looksLikeNinetyDayPlanName(opt.dataset.planName || opt.textContent);
  }

  function pickNonNinetyDayPlan(planId) {
    const id = String(planId || '').trim();
    if (!id || isNinetyDaySellingPlanId(id)) return null;
    return id;
  }

  function getThemeStaySellingPlanId() {
    const themeStay = (
      document.querySelector('[data-theme-stay-selling-plan-id]')?.getAttribute('data-theme-stay-selling-plan-id') ||
      ''
    ).trim();
    if (themeStay && !isNinetyDaySellingPlanId(themeStay)) return themeStay;
    const stay = (
      document.querySelector('[data-stay-selling-plan-id]')?.getAttribute('data-stay-selling-plan-id') ||
      ''
    ).trim();
    if (stay && !isNinetyDaySellingPlanId(stay)) return stay;
    return null;
  }

  function getThirtyDayPlanIdFromIntervalSelect() {
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    if (!intervalSelect) return null;
    let thirty = null;
    let monthly = null;
    Array.from(intervalSelect.options || []).forEach((opt) => {
      if (!opt.value) return;
      const id = String(opt.value);
      if (isNinetyDaySellingPlanId(id)) return;
      const days = parseInt(opt.dataset.intervalDays, 10);
      const name = opt.dataset.planName || opt.textContent || '';
      if (days === 30) thirty = id;
      else if (looksLikeThirtyDayPlanName(name)) monthly = monthly || id;
    });
    return thirty || monthly;
  }

  /**
   * 30-day subscription offer on this buy box.
   * Parent can be 90-day; upsells still use this plan, code, and percent.
   * Never returns a 90-day selling plan id.
   */
  function getThirtyDaySubscriptionOffer() {
    const selected = getSelectedFrequencyOption();
    const root = selected
      ? (selected.closest('.PBFCM-PDP-frequency-selector--configurable') || document)
      : document;
    const options = root.querySelectorAll('[data-frequency-id][data-option-kind="subscription"]');
    let match = null;
    options.forEach((opt) => {
      const days = parseInt(opt.dataset.days, 10);
      const period = String(opt.dataset.frequencyPeriod || '');
      if (days === 30 && period !== 'quarterly') match = opt;
    });
    if (!match) {
      options.forEach((opt) => {
        if (match) return;
        const days = parseInt(opt.dataset.days, 10);
        const blockQty = parseInt(opt.dataset.blockQty, 10);
        const period = String(opt.dataset.frequencyPeriod || '');
        if (period === 'quarterly' || days === 90) return;
        if (period === 'monthly' || blockQty === 1) match = opt;
      });
    }

    const matchPlan = match ? (match.dataset.sellingPlan || '').trim() : '';
    const intervalPlan = getThirtyDayPlanIdFromIntervalSelect();
    const monthlyCard = document.getElementById('PBFCM-frequency-monthly');
    const monthlyPlan = monthlyCard ? (monthlyCard.dataset.sellingPlan || '').trim() : '';
    const candidates = [matchPlan, intervalPlan, monthlyPlan, getThemeStaySellingPlanId()];
    let sellingPlanId = null;
    candidates.forEach((id) => {
      if (sellingPlanId) return;
      sellingPlanId = pickNonNinetyDayPlan(id);
    });

    const sectionPct = parseExplicitDiscountPercent(
      document.getElementById('PBFCM-sub-discount-percentage')?.value
    );
    const optionPct = match
      ? parseExplicitDiscountPercent(match.dataset.displayDiscountPercent)
      : null;
    const discountPercent = optionPct != null ? optionPct : sectionPct;

    return {
      sellingPlanId,
      coupon: match ? (match.dataset.atcCoupon || '').trim() : '',
      discountPercent
    };
  }

  /**
   * 30-day / monthly only. Sibling 90-day plan id comes from frequency option
   * data (configurable quarterly card or auto-fetched 90-day plan). No 90-day
   * sibling → stamp neither property.
   */
  function resolvePlanUpgradeToId(props, isAddon, isSubscription) {
    if (!isSubscription || isExcludedPlanUpgradeLine(props, isAddon)) return null;

    const selected = getSelectedFrequencyOption();
    if (selected) {
      const kind = (selected.dataset.optionKind || selected.dataset.frequency || '').toLowerCase();
      if (kind === 'otp' || kind === 'onetime') return null;
      const period = (selected.dataset.frequencyPeriod || '').toLowerCase();
      const upgradeTo = (selected.dataset.planUpgradeTo || '').trim();
      if (period === 'monthly' && upgradeTo) return upgradeTo;
      // 90-option LPs often omit data-plan-upgrade-to (offer_plan_upgrade off).
      // Still stamp the pill when a quarterly sibling exists.
      if (period === 'monthly') return inferNinetyDaySiblingPlanId();
      return null;
    }

    const monthlyCard = document.getElementById('PBFCM-frequency-monthly');
    const autoRoot = document.querySelector('.PBFCM-PDP-frequency-selector--auto');
    const upgradeTo = (
      (monthlyCard && monthlyCard.dataset.planUpgradeTo) ||
      (autoRoot && autoRoot.dataset.planUpgradeTo) ||
      inferNinetyDaySiblingPlanId() ||
      ''
    ).trim();
    if (!upgradeTo) return null;

    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    if (intervalSelect && intervalSelect.options && intervalSelect.selectedIndex >= 0) {
      const opt = intervalSelect.options[intervalSelect.selectedIndex];
      const days = parseInt(opt && opt.dataset.intervalDays, 10);
      if (Number.isFinite(days) && days !== 30) return null;
    }

    const thirtyDayId = (
      (monthlyCard && monthlyCard.dataset.planUpgradeFrom) ||
      (autoRoot && autoRoot.dataset.planUpgradeFrom) ||
      ''
    ).trim();
    const currentPlan = String(
      resolveSellingPlanIdForCart() || state.sellingPlanId || ''
    ).trim();
    if (thirtyDayId && currentPlan && currentPlan !== thirtyDayId) return null;

    return upgradeTo;
  }

  function inferNinetyDaySiblingPlanId() {
    const quarterly = getQuarterlyFrequencyOption();
    const fromCard = quarterly && (quarterly.dataset.sellingPlan || '').trim();
    if (fromCard) return fromCard;
    const intervalSelect = document.getElementById(CONFIG.IDS.SUBSCRIPTION_INTERVAL);
    const ninetyOpt = Array.from((intervalSelect && intervalSelect.options) || []).find((opt) => {
      const days = parseInt(opt.dataset.intervalDays, 10);
      return days === 90 || looksLikeNinetyDayPlanName(opt.dataset.planName || opt.textContent);
    });
    return ninetyOpt && ninetyOpt.value ? String(ninetyOpt.value) : null;
  }

  function getQuarterlyFrequencyOption() {
    return document.querySelector(
      '.PBFCM-PDP-frequency-selector--configurable [data-frequency-period="quarterly"], .PBFCM-PDP-frequency-selector--configurable [data-days="90"]'
    );
  }

  function resolvePlanUpgradeSavingsPercent() {
    const selected = getSelectedFrequencyOption();
    const monthlyPct = selected
      ? parseFloat(selected.dataset.displayDiscountPercent)
      : NaN;
    const quarterly = getQuarterlyFrequencyOption();
    const quarterlyPct = quarterly
      ? parseFloat(quarterly.dataset.displayDiscountPercent)
      : NaN;
    if (Number.isFinite(monthlyPct) && Number.isFinite(quarterlyPct) && quarterlyPct > monthlyPct) {
      return Math.round(quarterlyPct - monthlyPct);
    }
    return 20;
  }

  function resolvePlanUpgradeCode() {
    const selected = getSelectedFrequencyOption();
    const fromSelected = selected && (selected.dataset.planUpgradeCode || '').trim();
    if (fromSelected) return fromSelected;
    const monthlyCard = document.getElementById('PBFCM-frequency-monthly');
    const autoRoot = document.querySelector('.PBFCM-PDP-frequency-selector--auto');
    const fromMonthlyOrAuto = (
      (monthlyCard && monthlyCard.dataset.planUpgradeCode) ||
      (autoRoot && autoRoot.dataset.planUpgradeCode) ||
      ''
    ).trim();
    if (fromMonthlyOrAuto) return fromMonthlyOrAuto;
    const quarterly = getQuarterlyFrequencyOption();
    return (quarterly && (quarterly.dataset.atcCoupon || '').trim()) || '';
  }

  function resolvePlanUpgradeDiscountPercent() {
    const selected = getSelectedFrequencyOption();
    const fromSelected = selected
      ? parseFloat(selected.dataset.planUpgradeDiscountPercent)
      : NaN;
    if (Number.isFinite(fromSelected) && fromSelected > 0 && fromSelected <= 100) {
      return Math.round(fromSelected);
    }
    const monthlyCard = document.getElementById('PBFCM-frequency-monthly');
    const autoRoot = document.querySelector('.PBFCM-PDP-frequency-selector--auto');
    const fromMonthlyOrAuto = parseFloat(
      (monthlyCard && monthlyCard.dataset.planUpgradeDiscountPercent) ||
      (autoRoot && autoRoot.dataset.planUpgradeDiscountPercent) ||
      ''
    );
    if (Number.isFinite(fromMonthlyOrAuto) && fromMonthlyOrAuto > 0 && fromMonthlyOrAuto <= 100) {
      return Math.round(fromMonthlyOrAuto);
    }
    const quarterly = getQuarterlyFrequencyOption();
    const quarterlyPct = quarterly
      ? parseFloat(quarterly.dataset.displayDiscountPercent)
      : NaN;
    if (Number.isFinite(quarterlyPct) && quarterlyPct > 0 && quarterlyPct <= 100) {
      return Math.round(quarterlyPct);
    }
    return null;
  }

  function applyPlanUpgradeProperties(props, isAddon, isSubscription) {
    if (!props) return props;
    const upgradeTo = resolvePlanUpgradeToId(props, isAddon, isSubscription);
    if (!upgradeTo) return props;
    props[CONFIG.PROPERTIES.PLAN_UPGRADE] = 'true';
    props[CONFIG.PROPERTIES.PLAN_UPGRADE_TO] = String(upgradeTo);
    props[CONFIG.PROPERTIES.PLAN_UPGRADE_SAVINGS] = String(resolvePlanUpgradeSavingsPercent());
    const upgradeCode = resolvePlanUpgradeCode();
    if (upgradeCode) {
      props[CONFIG.PROPERTIES.PLAN_UPGRADE_CODE] = upgradeCode;
    }
    const upgradePct = resolvePlanUpgradeDiscountPercent();
    if (upgradePct != null) {
      props[CONFIG.PROPERTIES.PLAN_UPGRADE_DISCOUNT_PERCENTAGE] = String(upgradePct);
    }
    return props;
  }

  /**
   * Stamp per-option ATC coupon + display % onto line properties.
   * Wins over page-level __pdpDiscountOverride so 90-day / multi-box LPs can
   * carry distinct codes while Stay first-order discount stays at 0.
   * Subscription upsells always take the 30-day offer, not the parent option.
   */
  function applyBuyBoxDiscountProperties(props, isSubscription, isAddon = false) {
    if (!props) return props;
    // Catalog PDPs keep the latched funnel codes. Funnel LPs still stamp
    // per-option ATC coupons (Stay first-order 0, 90-day, etc.).
    if (window.FDOffer && typeof window.FDOffer.overridesCatalogPdp === 'function' &&
        window.FDOffer.overridesCatalogPdp(window.location && window.location.pathname)) {
      return props;
    }
    const isUpsell = props[CONFIG.PROPERTIES.UPSELL_ITEM] === 'true' ||
      props[CONFIG.PROPERTIES.UPSELL_ITEM] === true;
    let coupon = (state.optionAtcCoupon || '').trim();
    let pct = state.optionDisplayDiscountPercent;
    if (isUpsell && isSubscription && !isAddon) {
      const thirty = getThirtyDaySubscriptionOffer();
      coupon = thirty.coupon;
      if (thirty.discountPercent != null) pct = thirty.discountPercent;
    }
    if (coupon) {
      // Stamp both keys so OTP↔sub upgrade / checkout redirect still resolve a code
      props[CONFIG.PROPERTIES.SUB_DISCOUNT_CODE] = coupon;
      props[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] = coupon;
    }
    if (Number.isFinite(pct) && pct >= 0 && pct <= 100) {
      const pctStr = String(pct);
      if (isSubscription) {
        props[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE] = pctStr;
      } else {
        props[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE] = pctStr;
      }
      // Also stamp the sibling % so cart FE can show savings after upgrade toggles
      if (isSubscription && !props[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE]) {
        props[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE] = pctStr;
      } else if (!isSubscription && !props[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE]) {
        props[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE] = pctStr;
      }
    }
    const pdpOverride = (typeof window !== 'undefined' && window.__pdpDiscountOverride) || null;
    const otpCode = props[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] || coupon;
    if (pdpOverride && pdpOverride.autoApplyOnOtp === true && otpCode && !isSubscription && !isAddon && !isUpsell) {
      props[CONFIG.PROPERTIES.AUTO_APPLY_OTP_DISCOUNT_CODE] = 'true';
    }
    return props;
  }

  function createItemData({ variantId, quantity, isSubscription, sellingPlanId = null, upgradeSellingPlanId = null, properties = {}, isAddon = false }) {
    const isUpsell = properties && (
      properties[CONFIG.PROPERTIES.UPSELL_ITEM] === 'true' ||
      properties[CONFIG.PROPERTIES.UPSELL_ITEM] === true
    );
    let resolvedInputPlanId = sellingPlanId ||
      (isSubscription
        ? (isUpsell ? getThirtyDaySubscriptionOffer().sellingPlanId : resolveSellingPlanIdForCart())
        : null);
    if (isUpsell && isNinetyDaySellingPlanId(resolvedInputPlanId)) {
      resolvedInputPlanId = pickNonNinetyDayPlan(getThirtyDaySubscriptionOffer().sellingPlanId);
    }
    if (isUpsell && isSubscription && !resolvedInputPlanId) {
      isSubscription = false;
    }
    let resolvedUpgradePlanId = upgradeSellingPlanId ||
      (!isSubscription ? resolveUpgradeSellingPlanId(variantId) : null);
    if (isUpsell && isNinetyDaySellingPlanId(resolvedUpgradePlanId)) {
      resolvedUpgradePlanId = pickNonNinetyDayPlan(getThirtyDaySubscriptionOffer().sellingPlanId);
    }
    const lineProperties = Object.assign({}, properties);
    if (isAddon) lineProperties._add_on = 'true';
    applyPlanUpgradeProperties(lineProperties, isAddon, isSubscription);

    console.log('📦 [PBFCM-PDP] Creating item data:', { variantId, quantity, isSubscription, isAddon, resolvedUpgradePlanId });

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.createItemData === 'function') {
      const item = window.CartUtilities.createItemData({
        variantId,
        quantity,
        isSubscription,
        properties: lineProperties,
        sellingPlanId: resolvedInputPlanId,
        upgradeSellingPlanId: resolvedUpgradePlanId,
        isAddon
      });
      if (item && item.properties) {
        applyBuyBoxDiscountProperties(item.properties, isSubscription, isAddon);
      }
      return item;
    }
      console.log('📦 [PBFCM-PDP] Creating item data2:', { variantId, quantity, isSubscription, isAddon });

    // Fallback to local implementation
    const baseProperties = {
      [CONFIG.PROPERTIES.FULL_PRICE]: "true",
      [CONFIG.PROPERTIES.PRODUCT_TYPE]: "rebrand",
      ...lineProperties
    };

    // Mirror cart-utilities.js: stamp PDP discount metadata when the override is present.
    const pdpOverride = (typeof window !== 'undefined' && window.__pdpDiscountOverride) || null;
    if (pdpOverride) {
      const otpCode = pdpOverride.otpCouponCode || pdpOverride.couponCode || '';
      const subCode = pdpOverride.couponCode || '';
      if (otpCode) baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] = String(otpCode);
      if (subCode) baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_CODE] = String(subCode);
      if (Number.isFinite(pdpOverride.discountPercent)) {
        baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE] = String(pdpOverride.discountPercent);
      }
      // Prefer the section's own sub_discount_percentage (frequency selector hidden input)
      // so the line item remembers the exact sub discount that was configured on the PDP
      // it was added from. Falling back to OTP % would re-discount sub lines whenever
      // the merchant uses a sub-only discount without a separate OTP code. Latched
      // funnel offers on catalog PDPs use the patched override instead.
      const offerOverridesCatalog = window.FDOffer &&
        typeof window.FDOffer.overridesCatalogPdp === 'function' &&
        window.FDOffer.overridesCatalogPdp(window.location && window.location.pathname);
      const sectionSubPctEl = offerOverridesCatalog
        ? null
        : document.getElementById('PBFCM-sub-discount-percentage');
      const sectionSubPctRaw = sectionSubPctEl ? parseFloat(sectionSubPctEl.value) : NaN;
      const sectionSubPct = (Number.isFinite(sectionSubPctRaw) && sectionSubPctRaw > 0 && sectionSubPctRaw <= 100)
        ? sectionSubPctRaw
        : null;
      const subPct = sectionSubPct !== null
        ? sectionSubPct
        : (Number.isFinite(pdpOverride.subDiscountPercent)
            ? pdpOverride.subDiscountPercent
            : (Number.isFinite(pdpOverride.discountPercent) ? pdpOverride.discountPercent : null));
      if (subPct !== null) {
        baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE] = String(subPct);
      }
    }

    applyBuyBoxDiscountProperties(baseProperties, isSubscription, isAddon);

    const item = {
      id: variantId,
      quantity: quantity,
      properties: baseProperties
    };

    let resolvedPlanId = null;
    if (isSubscription) {
      resolvedPlanId = resolvedInputPlanId ||
                      (isAddon ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
      if (resolvedPlanId) {
        item.selling_plan = resolvedPlanId;
      }
      item.properties[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = true;
    } else {
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
    }

    // Stamp _if_upgrade__selling_plan_id even on OTP items, using the plan that would
    // be applied on upgrade-to-sub. Lets cart.json readers skip a /products/{handle}.js
    // lookup. It is upgrade metadata only, not the active subscription binding.
    const planForProperty = resolvedPlanId ||
                            resolvedUpgradePlanId ||
                            (isAddon ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
    if (planForProperty) {
      item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID] = String(planForProperty);
    }

    console.log('📦 [PBFCM-PDP] Item data created:', item);
    return item;
  }

  /**
   * Stamp Upsell Card metaobject Coupon onto a cart line after createItemData.
   * Must run AFTER createItemData — that helper overwrites codes from __pdpDiscountOverride.
   */
  function applyUpsellCouponToItem(item, coupon, isSubscription) {
    if (!item || !coupon) {
      return item;
    }
    const code = String(coupon).trim();
    if (!code) {
      return item;
    }
    item.properties = item.properties || {};
    item.properties[CONFIG.PROPERTIES.UPSELL_DISCOUNT_CODE] = code;
    if (isSubscription) {
      item.properties[CONFIG.PROPERTIES.SUB_DISCOUNT_CODE] = code;
    } else {
      item.properties[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] = code;
    }
    return item;
  }

  /**
   * Apply discount codes on the cart itself (Ajax) so checkout already has them,
   * not only via /checkout?discount=… (which is easy to miss / strip).
   */
  // The cart this page already holds when no cart write happened since it came
  // back from the server, else a fresh read.
  async function currentCart() {
    const store = window.FDCart;
    const cached = store && typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null;
    if (cached) return cached;
    if (window.FDCartApi) return window.FDCartApi.getCart(true);
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    return res.ok ? res.json() : null;
  }

  async function applySkidkaCodesToCart(codes) {
    const unique = Array.from(new Set((codes || []).map(function (c) {
      return String(c || '').trim();
    }).filter(Boolean)));
    if (!unique.length) return null;

    try {
      const cart = await currentCart();
      const existing = (cart.discount_codes || []).map(function (d) { return d && d.code; }).filter(Boolean);
      const merged = Array.from(new Set(existing.concat(unique)));
      if (merged.length === existing.length) return cart;

      if (window.FDCartApi && typeof window.FDCartApi.updateCart === 'function') {
        return await window.FDCartApi.updateCart({ discount: merged.join(',') });
      }
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: merged.join(',') })
      });
      return await updateRes.json();
    } catch (err) {
      return null;
    }
  }

  function getAutoApplyOtpDiscountCodesFromItems(items) {
    const codes = [];
    (items || []).forEach(function (item) {
      const props = (item && item.properties) || {};
      const shouldAutoApply = props[CONFIG.PROPERTIES.AUTO_APPLY_OTP_DISCOUNT_CODE] === 'true' ||
        props[CONFIG.PROPERTIES.AUTO_APPLY_OTP_DISCOUNT_CODE] === true;
      const code = props[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE];
      if (shouldAutoApply && code) {
        codes.push(code);
      }
    });
    return codes;
  }

  async function clearCartForStraightToCheckout() {
    if (!CONFIG.STRAIGHT_TO_CHECKOUT) return null;

    const routeRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${routeRoot}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      let errBody = null;
      try { errBody = await response.clone().json(); } catch (e) {}
      console.error('❌ [PBFCM-PDP] Cart clear failed:', response.status, errBody);
      throw new Error((errBody && (errBody.description || errBody.message)) || 'Failed to clear cart');
    }

    const clearedCart = await response.json();
    console.log('🧹 [PBFCM-PDP] Cleared existing cart items for straight-to-checkout flow:', clearedCart);
    return clearedCart;
  }

  /**
   * 30-day / Stay only. Never the parent 90-day plan, first-allocation 90-day,
   * or section stay when that stay is the 90-day option on this LP.
   */
  function resolveUpsellSellingPlan(upsellInfo, isSubscription) {
    if (!isSubscription) return { sellingPlanId: null, isSubscription: false };
    if (upsellInfo && upsellInfo.isAddon) {
      const addonPlan = pickNonNinetyDayPlan(upsellInfo.sellingPlanId);
      if (addonPlan) return { sellingPlanId: addonPlan, isSubscription: true };
    }
    const thirtyDayPlanId = pickNonNinetyDayPlan(getThirtyDaySubscriptionOffer().sellingPlanId);
    if (thirtyDayPlanId) {
      return { sellingPlanId: thirtyDayPlanId, isSubscription: true };
    }
    const fromCard = pickNonNinetyDayPlan(upsellInfo && upsellInfo.sellingPlanId);
    if (fromCard) {
      return { sellingPlanId: fromCard, isSubscription: true };
    }
    console.warn('⚠️ [PBFCM-PDP] Upsell has no valid 30-day selling plan; adding as OTP to avoid 90-day:', upsellInfo && upsellInfo.title);
    return { sellingPlanId: null, isSubscription: false };
  }

  /**
   * Show OTP convert dialog and handle user response
   * @returns {Promise<boolean>} - true if user chose to subscribe, false if declined
   */
  function showOTPConvertDialog() {
    return new Promise((resolve) => {
      const dialog = document.querySelector('otp-convert-dialog');

      if (!dialog) {
        console.warn('⚠️ [PBFCM-PDP] OTP Convert Dialog not found, proceeding with OTP');
        resolve(false);
        return;
      }

      dialog.show({
        onSubscribe: () => {
          console.log('🔄 [PBFCM-PDP] User chose to subscribe');
          resolve(true);
        },
        onDecline: () => {
          console.log('🛒 [PBFCM-PDP] User declined subscription, proceeding with OTP');
          resolve(false);
        }
      });
    });
  }

  function applyOfferToDialogProducts(upsellProducts, isSubscription) {
    if (selectedOptionIsExplicitZero()) return upsellProducts;
    const offer = window.FDOffer && window.FDOffer.active;
    if (!offer || !offer.replacesGlobal) return upsellProducts;
    if (window.FDOffer.overridesCatalogPdp &&
        !window.FDOffer.overridesCatalogPdp(window.location && window.location.pathname)) {
      return upsellProducts;
    }
    const pct = isSubscription ? offer.subPct : offer.otpPct;
    if (!Number.isFinite(pct)) return upsellProducts;
    return upsellProducts.map((product) => {
      const compare = Number(product.comparePrice || product.price) || 0;
      if (!compare) return product;
      const sale = Math.round(compare * (100 - pct) / 100);
      const next = { ...product, price: sale, comparePrice: compare };
      if (isSubscription) next.subscriptionPrice = sale;
      else next.onetimePrice = sale;
      return next;
    });
  }

  /**
   * Show upsell dialog and handle user response
   * @param {boolean} isSubscription - Whether this is a subscription purchase
   * @param {Function} commitSelection - Adds pending products and reports success
   * @returns {Promise<Object>} - { success, navigateTo? }
   */
  function showUpsellDialog(isSubscription, commitSelection) {
    return new Promise((resolve) => {
      const dialog = document.querySelector('upsell-dialog');

      if (!dialog) {
        console.warn('⚠️ [PBFCM-PDP] Upsell Dialog not found, proceeding without upsells');
        commitSelection({}).then(success => resolve({ success }));
        return;
      }

      // Get upsell products data from JSON script
      const upsellProductsDataEl = document.getElementById('PBFCM-upsell-products-data');
      const giftConfigDataEl = document.getElementById('PBFCM-gift-config-data');

      let upsellProducts = [];
      let giftConfig = null;

      try {
        if (upsellProductsDataEl) {
          upsellProducts = JSON.parse(upsellProductsDataEl.textContent);
        }
        if (giftConfigDataEl) {
          giftConfig = JSON.parse(giftConfigDataEl.textContent);
        }
      } catch (e) {
        console.error('❌ [PBFCM-PDP] Error parsing upsell/gift data:', e);
      }

      upsellProducts = syncDialogUpsellProductsWithLivePdp(upsellProducts, isSubscription);
      upsellProducts = applyOfferToDialogProducts(upsellProducts, isSubscription);

      // Build main product data
      const mainProduct = {
        id: state.productId,
        variantId: state.variantId,
        title: document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() || 'Product',
        price: Math.round(state.prices.base * 100),
        subscriptionPrice: Math.round(state.prices.subscription * 100),
        comparePrice: Math.round(state.prices.base * 100),
        quantity: state.quantity
      };

      console.log('🎁 [PBFCM-PDP] Showing upsell dialog with:', {
        mainProduct,
        upsellProducts,
        giftConfig,
        isSubscription
      });

      dialog.show({
        mainProduct: mainProduct,
        upsellProducts: upsellProducts,
        giftConfig: giftConfig,
        isSubscription: isSubscription,
        preselectedIndex: giftConfig?.preselectedUpsellIndex ?? -1,
        onCheckout: async (selectedUpsells, options = {}) => {
          const success = await commitSelection(selectedUpsells, options);
          // The cart drawer may have closed the dialog at the click: settle either way.
          if (success || !dialog.classList.contains(dialog.classes.activeClass)) {
            if (success) dialog.hide();
            resolve({ success, navigateTo: options.navigateTo });
          }
          return success;
        },
        onDecline: async () => {
          const success = await commitSelection({});
          if (success || !dialog.classList.contains(dialog.classes.activeClass)) {
            if (success) dialog.hide();
            resolve({ success });
          }
          return success;
        }
      });
    });
  }

  // Single-flight guard: a second click while a previous ATC is awaiting
  // dialogs or /cart/add.js returns the in-flight promise instead of firing
  // a parallel request that could race on Shopify's cart lock.
  let addToCartInFlight = null;

  /**
   * Main add to cart handler
   * Based on separate-buy-buttons-v4-no-bulk.liquid handleAddToCart function
   */
  async function handleAddToCart() {
    if (addToCartInFlight) {
      console.log('🛒 [PBFCM-PDP] Add to cart already in flight; coalescing duplicate trigger');
      return addToCartInFlight;
    }

    addToCartInFlight = (async () => {
      return _handleAddToCartImpl();
    })().finally(() => {
      addToCartInFlight = null;
    });

    return addToCartInFlight;
  }

  async function _handleAddToCartImpl() {
    console.log('🛒 [PBFCM-PDP] Starting add to cart');

    const isOTP = state.frequency !== 'monthly';

    // Step 1: Show OTP convert dialog if one-time purchase
    if (isOTP) {
      console.log('🔔 [PBFCM-PDP] One-time purchase detected, showing convert dialog');
      const shouldSubscribe = await showOTPConvertDialog();

      if (shouldSubscribe) {
        // Prefer first subscription option in configurable mode
        const subOpt = document.querySelector(
          '.PBFCM-PDP-frequency-selector--configurable [data-option-kind="subscription"]'
        );
        if (subOpt) {
          selectFrequencyOption(subOpt);
        } else {
          selectFrequency('monthly');
        }
      }
      // If declined, proceed with OTP (state.frequency unchanged)
    }

    // Step 2: Upsell dialog — legacy Liquid/CSS, or per-option trigger_upsell
    const optionUpsell = selectedOptionTriggersUpsell();
    const { liquidDialogMode, isPdpHiddenByCSS, useUpsellDialog: baseUpsell } = getUpsellDialogModeState();
    const useUpsellDialog = optionUpsell === true
      ? true
      : (optionUpsell === false ? false : baseUpsell);

    if (isPdpHiddenByCSS && !liquidDialogMode) {
      console.log('🧪 [PBFCM-PDP] A/B variant detected: PDP elements hidden via CSS, triggering dialog mode');
    }

    const progress = {};
    let pageAdd = null;
    const commitSelection = async (selectedUpsells, options = {}) => {
      // The drawer opens at the click (or the dialog's Add) with the new lines as
      // placeholders, and paints once the add is confirmed (updateCartAfterAdd).
      if (!pageAdd && !options.navigateTo && !CONFIG.STRAIGHT_TO_CHECKOUT && window.FDCartPageAdd) {
        const dialog = useUpsellDialog ? document.querySelector('upsell-dialog') : null;
        if (dialog && typeof dialog.hide === 'function' && dialog.classList.contains(dialog.classes.activeClass)) dialog.hide();
        pageAdd = window.FDCartPageAdd.begin(pendingLinesForAdd(selectedUpsells));
      }
      const success = await addPendingCartItems(useUpsellDialog, selectedUpsells, progress);
      if (!success && pageAdd) {
        pageAdd.fail();
        pageAdd = null;
      }
      return success;
    };
    const result = useUpsellDialog
      ? await showUpsellDialog(state.frequency === 'monthly', commitSelection)
      : { success: await commitSelection({}) };

    if (result.success && !result.navigateTo) {
      await updateCartAfterAdd(pageAdd);
    }
  }

  // Placeholder lines for the cart drawer while the add runs: the main product and
  // the dialog upsells picked, with the product images the page already has.
  function pendingLinesForAdd(dialogUpsells) {
    const imagesById = window.__productFeaturedImagesById || {};
    const imagesByHandle = window.__productFeaturedImagesByHandle || {};
    const mainTitle = document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() ||
      CONFIG.BUNDLE_NAME || '';
    const lines = [{
      title: mainTitle,
      image: imagesById[String(state.productId)] || '',
      quantity: state.quantity * (state.blockQty || 1)
    }];
    Object.keys(dialogUpsells || {}).forEach(productId => {
      const upsell = dialogUpsells[productId];
      if (!upsell || !(upsell.quantity > 0)) return;
      lines.push({
        title: upsell.title || '',
        image: imagesById[String(productId)] || imagesByHandle[upsell.handle] || '',
        quantity: upsell.quantity
      });
    });
    return lines;
  }

  async function addPendingCartItems(useUpsellDialog, dialogUpsells, progress) {
    // Step 3: Show loading state and add to cart
    setLoading(true);

    try {
      const isSubscription = state.frequency === 'monthly';

      if (!progress.started) {
        await clearCartForStraightToCheckout();
        progress.started = true;
      }

      const items = [];

      const bundleVariants = CONFIG.BUNDLE_VARIANT_IDS.filter((variantId) => variantId && variantId !== '');

      // Starter slots, filled from the same Bundle entry (used if the bundle JSON fails to parse)
      const starterVariants = [
        CONFIG.STARTER_METAFIELDS.multivitamin,
        CONFIG.STARTER_METAFIELDS.magnesium,
        CONFIG.STARTER_METAFIELDS.biotic
      ].filter(variantId => variantId && variantId !== '');

      const variantsToAdd = bundleVariants.length > 0 ? bundleVariants : starterVariants;

      const effectiveQty = state.quantity * (state.blockQty || 1);
      const blockQtyProp = (state.blockQty || 1) > 1 ? String(state.blockQty) : null;

      if (variantsToAdd.length > 0) {
        const bundleGroupId = uniqueBundleId();
        const bundleTitle = CONFIG.BUNDLE_NAME || '';
        const parentPdpId = state.productId != null ? String(state.productId) : '';
        const pagePath = window.location.pathname;
        const handleMatch = pagePath.match(/\/products\/([^/?#]+)/);
        const parentPdpHandle = CONFIG.BUNDLE_CART_DATA?.productHandle || (handleMatch ? handleMatch[1] : '');

        variantsToAdd.forEach(variantId => {
          const preventSub = shouldPreventSubscription(parseInt(variantId));
          const effectiveSubscription = preventSub ? false : isSubscription;
          if (preventSub) {
            console.log('[preventSubscriptionOfMetafieldVariant] [handleAddToCart] removing selling_plan from variant:', variantId);
          }

          const starterProperties = {
            _bundle: bundleGroupId,
            _bundle_title: bundleTitle,
            _parent_pdp: parentPdpId,
            _parent_pdp_handle: parentPdpHandle,
            _page_path: pagePath,
            _bundle_name: CONFIG.BUNDLE_NAME
          };
          if (preventSub) {
            starterProperties._prevent_subscription = 'true';
          }
          if (blockQtyProp) {
            starterProperties[CONFIG.PROPERTIES.QTY_PER_CHANGE] = blockQtyProp;
          }

          items.push(createItemData({
            variantId: parseInt(variantId),
            quantity: effectiveQty,
            isSubscription: effectiveSubscription,
            properties: starterProperties
          }));
        });
      } else {
        // No starter variants - add main product as normal
        console.log('🛒 [PBFCM-PDP] No starter variants, adding main product');
        const mainProps = {};
        if (blockQtyProp) {
          mainProps[CONFIG.PROPERTIES.QTY_PER_CHANGE] = blockQtyProp;
        }
        items.push(createItemData({
          variantId: state.variantId,
          quantity: effectiveQty,
          isSubscription: isSubscription,
          sellingPlanId: isSubscription ? resolveSellingPlanIdForCart() : null,
          upgradeSellingPlanId: resolveUpgradeSellingPlanId(state.variantId),
          properties: mainProps
        }));
      }

      // Add upsell products - either from dialog (if enabled) or from inline upsells
      const upsellsToAdd = useUpsellDialog ? dialogUpsells : state.upsells;

      // Handle dialog upsells (different data structure)
      if (useUpsellDialog && Object.keys(dialogUpsells).length > 0) {
        // Enforce one free gift per ORDER: if the cart already contains a
        // free-unlock line, don't add another. Only bother checking when a
        // selected upsell actually carries a free unlock.
        let freeGiftAlreadyOnOrder = false;
        const anyFreeUnlock = Object.values(dialogUpsells).some(
          u => u && u.quantity > 0 && u.freeUnlock && u.freeUnlock.variantId
        );
        if (anyFreeUnlock) {
          try {
            const cartData = await currentCart();
            if (cartData) {
              freeGiftAlreadyOnOrder = (cartData.items || []).some(li => {
                const p = li.properties || {};
                return p._free_unlock === 'true' || p._free_unlock === true;
              });
            }
          } catch (e) {
            console.warn('🎁 [PBFCM-PDP] Could not verify existing free gift in cart', e);
          }
        }

        Object.keys(dialogUpsells).forEach(productId => {
          const upsellInfo = dialogUpsells[productId];
          if (upsellInfo && upsellInfo.quantity > 0) {
            // Free "unlocks" attachment → add the separate gifted product as its
            // own line alongside this upsell. Price is driven to $0 via the same
            // promo-price property mechanism the other upsells use (set to 0).
            // NOTE: the actual $0 must be honored by the store's add-on pricing
            // discount/function — verify on a test order before launch.
            // Only ONE free gift per order — quantity is always 1 regardless of
            // how many of the trigger product are added, and we skip entirely if
            // the cart already has a free-unlock line (freeGiftAlreadyOnOrder).
            const freeUnlock = upsellInfo.freeUnlock;
            if (freeUnlock && freeUnlock.variantId && !freeGiftAlreadyOnOrder) {
              const freeProps = {
                [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                _upsell_module: 'pdp_upsells_dialog',
                _page_path: window.location.pathname,
                _free_unlock: 'true',
                _unlocked_by: upsellInfo.title || '',
                [CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE]: '0'
              };
              if (freeUnlock.comparePrice > 0) {
                freeProps[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(freeUnlock.comparePrice));
              }
              // Stamp the trigger's discount code onto the gift line itself. The
              // checkout redirect (subscription-checkout-redirect.liquid) reads
              // this off the cart and merges it with the section's auto-apply
              // code into a single /discount/A,B request, so BOTH apply. Keeping
              // it on the line also means removing the gift removes the code.
              if (freeUnlock.discountCode) {
                freeProps._unlock_discount_code = String(freeUnlock.discountCode);
              }
              // Mark handled so a second selected trigger in the same dialog
              // can't add another free gift.
              freeGiftAlreadyOnOrder = true;
              items.push(createItemData({
                variantId: parseInt(freeUnlock.variantId, 10),
                quantity: 1,
                isSubscription: false,
                properties: freeProps,
                isAddon: true
              }));
            }

            // Bundle / starter-pack upsell → expand into one line item per
            // component, mirroring the main-product starter logic (lines ~1336).
            // Selling plan follows the SAME rule the upsell module uses for every
            // other upsell (add-on → addon plan, else Stay plan). Per-component
            // pricing comes from the dialog data (computed in Liquid).
            const bundleVariants = Array.isArray(upsellInfo.bundleVariants) ? upsellInfo.bundleVariants : [];
            if (bundleVariants.length > 0) {
              const bundlePlanResolve = resolveUpsellSellingPlan(upsellInfo, isSubscription);
              console.log(`📦 [PBFCM-PDP] Bundle upsell "${upsellInfo.title}" plan:`, bundlePlanResolve);
              // One group id per added pack so the mini-cart renders the components as one card
              // (same property set as the main-product starter flow and the product cards).
              const bundleGroupId = 'starter-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
              const bundleGroupTitle = upsellInfo.bundleName || upsellInfo.title || 'Starter Pack';
              bundleVariants.forEach(comp => {
                if (!comp || !comp.id) return;
                const compProps = {
                  [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                  _upsell_module: "pdp_upsells_dialog",
                  _page_path: window.location.pathname,
                  _bundle: bundleGroupId,
                  _bundle_title: bundleGroupTitle,
                  _bundle_name: upsellInfo.bundleName || upsellInfo.title || ''
                };
                if (upsellInfo.productId) compProps._parent_pdp = String(upsellInfo.productId);
                if (upsellInfo.handle) compProps._parent_pdp_handle = String(upsellInfo.handle);
                const compPromo = isSubscription ? comp.subscriptionPrice : comp.onetimePrice;
                if (compPromo > 0) {
                  compProps[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(compPromo));
                }
                if (comp.fullPrice > 0) {
                  compProps[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(comp.fullPrice));
                }
                const compPrevent = comp.prevent === true;
                if (compPrevent) {
                  compProps._prevent_subscription = 'true';
                  console.log('[preventSubscriptionOfMetafieldVariant] [dialog bundle] removing selling_plan from component:', comp.id);
                }
                // Components are their own variants. A pack-level plan 422s.
                // Use the component plan when Liquid resolved one, otherwise the
                // 30-day plan. Never the parent 90-day plan.
                const compSubscription = compPrevent ? false : isSubscription;
                const compPlanId = compSubscription
                  ? (comp.sellingPlanId
                    ? String(comp.sellingPlanId).trim()
                    : bundlePlanResolve.sellingPlanId)
                  : null;
                const compItem = createItemData({
                  variantId: parseInt(comp.id, 10),
                  quantity: upsellInfo.quantity,
                  isSubscription: compSubscription,
                  properties: compProps,
                  isAddon: upsellInfo.isAddon || false,
                  sellingPlanId: compPlanId
                });
                applyUpsellCouponToItem(compItem, upsellInfo.coupon, compSubscription);
                items.push(compItem);
              });
              return;
            }

            const upsellProperties = {
              [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
              _upsell_module: "pdp_upsells_dialog",
              _page_path: window.location.pathname
            };
            if (upsellInfo.price > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(upsellInfo.price));
            }
            if (upsellInfo.comparePrice > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(upsellInfo.comparePrice));
            }

            const planResolve = resolveUpsellSellingPlan(upsellInfo, isSubscription);
            console.log(`📦 [PBFCM-PDP] Selling plan for "${upsellInfo.title}":`, planResolve);

            const upsellItem = createItemData({
              variantId: upsellInfo.variantId,
              quantity: upsellInfo.quantity,
              isSubscription: planResolve.isSubscription,
              properties: upsellProperties,
              isAddon: upsellInfo.isAddon || false,
              sellingPlanId: planResolve.sellingPlanId
            });
            applyUpsellCouponToItem(upsellItem, upsellInfo.coupon, planResolve.isSubscription);
            items.push(upsellItem);
          }
        });
      } else {
        // Handle inline upsells (original behavior)
        Object.keys(state.upsells).forEach(productId => {
          const qty = state.upsells[productId];
          if (qty > 0) {
            const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
            if (upsellData) {
              console.log('🛒 [PBFCM-PDP] upsellData', upsellData);

              const variantId = parseInt(upsellData.dataset.variant);
              const priceData = getUpsellPriceData(upsellData);
              const upsellTitle = (upsellData.dataset.upsellTitle || '').toLowerCase();
              const isAddon = upsellData.dataset.isAddon === 'true' || upsellTitle.includes('add-on');
              const upsellProperties = {
                [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                _upsell_module: "pdp_upsells",
                _page_path: window.location.pathname
              };
              if (priceData.priceCents > 0) {
                upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(priceData.priceCents));
              }
              if (priceData.comparePriceCents > 0) {
                upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(priceData.comparePriceCents));
              }

              // Determine selling plan ID based on product title
              let sellingPlanId = null;
              if (isSubscription) {
                sellingPlanId = isAddon
                  ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID
                  : getThirtyDaySubscriptionOffer().sellingPlanId;
                console.log(`📦 [PBFCM-PDP] Selling plan for "${upsellTitle}": ${isAddon ? 'ADDON' : '30-day'} (${sellingPlanId})`);
              }

              items.push(createItemData({
                variantId: variantId,
                quantity: qty,
                isSubscription: isSubscription,
                properties: upsellProperties,
                isAddon: isAddon,
                sellingPlanId: sellingPlanId
              }));
            }
          }
        });
      }

      console.log('🛒 [PBFCM-PDP] Items to add:', items);

      if (window.FDCartContext && typeof window.FDCartContext.stampPlanUpgradeOnItems === 'function') {
        window.FDCartContext.stampPlanUpgradeOnItems(items);
      }

      items.forEach((item) => {
        const p = (item && item.properties) || {};
        const isUpsell = p[CONFIG.PROPERTIES.UPSELL_ITEM] === 'true' || p[CONFIG.PROPERTIES.UPSELL_ITEM] === true;
        if (!isUpsell) return;
        const thirty = pickNonNinetyDayPlan(getThirtyDaySubscriptionOffer().sellingPlanId);
        if (isNinetyDaySellingPlanId(item.selling_plan)) {
          if (thirty) {
            item.selling_plan = thirty;
          } else {
            delete item.selling_plan;
            p[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = false;
            p[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
          }
        }
        if (isNinetyDaySellingPlanId(p[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID])) {
          if (thirty) p[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID] = String(thirty);
          else delete p[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID];
        }
      });

      // Split main vs upsell so a bad upsell selling_plan 422 can't kill the main add.
      const mainItems = items.filter(function (item) {
        const p = (item && item.properties) || {};
        return p[CONFIG.PROPERTIES.UPSELL_ITEM] !== 'true' && p[CONFIG.PROPERTIES.UPSELL_ITEM] !== true;
      });
      const upsellItems = items.filter(function (item) {
        const p = (item && item.properties) || {};
        return p[CONFIG.PROPERTIES.UPSELL_ITEM] === 'true' || p[CONFIG.PROPERTIES.UPSELL_ITEM] === true;
      });

      // Welcome kit gifts: separate request so a bad/unpublished gift
      // variant (422) cannot roll back the main 30/90-day line.
      // When Alternate Quantity is active, its JSON config is the ONLY gift source.
      // This prevents hidden/legacy frequency gifts from being swept in from the DOM.
      const quantityGiftConfig = getQuantityWelcomeKitConfig();
      const quantityWelcomeKitGiftItems = quantityGiftConfig
        ? buildQuantityWelcomeKitGiftItems()
        : [];
      const standardWelcomeKitGiftItems = quantityGiftConfig
        ? []
        : buildWelcomeKitGiftItems();
      const welcomeKitGiftItems = mergeWelcomeKitGiftItems(
        standardWelcomeKitGiftItems,
        quantityWelcomeKitGiftItems
      );

      if (typeof window.addToCart === 'function') {
        if (mainItems.length > 0 && !progress.mainAdded) {
          const mainResponse = await window.addToCart({ items: mainItems });
          console.log('✅ [PBFCM-PDP] Main add response:', mainResponse);
          if (!mainResponse.ok) {
            let errBody = null;
            try { errBody = await mainResponse.clone().json(); } catch (e) {}
            console.error('❌ [PBFCM-PDP] Main add failed:', mainResponse.status, errBody);
            throw new Error((errBody && (errBody.description || errBody.message)) || 'Failed to add product to cart');
          }
          progress.mainAdded = true;
          await applySkidkaCodesToCart(getAutoApplyOtpDiscountCodesFromItems(mainItems));
        }

        if (upsellItems.length > 0 && !progress.upsellsAdded) {
          try {
            const upsellResponse = await window.addToCart({ items: upsellItems });
            if (!upsellResponse.ok) {
              let errBody = null;
              try { errBody = await upsellResponse.clone().json(); } catch (e) {}
              throw new Error((errBody && (errBody.description || errBody.message)) || 'Failed to add upsell to cart');
            }
            progress.upsellsAdded = true;
            const skidkaCodes = upsellItems
              .map(item => item.properties && item.properties._upsell_discount_code)
              .filter(Boolean);
            await applySkidkaCodesToCart(skidkaCodes);
          } catch (error) {
            if (useUpsellDialog) throw error;
            console.error('⚠️ [PBFCM-PDP] Upsell add failed (main kept):', error);
          }
        }

        await addWelcomeKitGiftsSeparately(welcomeKitGiftItems);

        return true;
      } else {
        console.error('❌ [PBFCM-PDP] window.addToCart not available');
        throw new Error('Cart is not available');
      }
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error adding to cart:', error);
      if (!useUpsellDialog) alert(window.cartStrings.error);
      return false;
    } finally {
      setLoading(false);
    }
  }

  /**
   * Update cart display after adding items
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  async function updateCartAfterAdd(pageAdd) {
    console.log('🛒 [PBFCM-PDP] Updating cart display');

    // Check if straight-to-checkout is enabled
    if (CONFIG.STRAIGHT_TO_CHECKOUT) {
      console.log('🚀 [PBFCM-PDP] Straight to checkout enabled, redirecting...');
      if (window.SubscriptionCheckout && typeof window.SubscriptionCheckout.redirectToCheckout === 'function') {
        window.SubscriptionCheckout.redirectToCheckout();
      } else {
        window.location.href = '/checkout';
      }
      return;
    }

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.updateCartAfterAdd === 'function') {
      return window.CartUtilities.updateCartAfterAdd(pageAdd);
    }

    // Fallback to local implementation
    // Open the cart drawer FIRST, independent of the /cart.js fetch below. The
    // add already succeeded; gating the open behind that fetch meant slow/flaky
    // mobile data delayed the open, or a failed fetch threw and skipped it.
    if (typeof toggleCartOpen === 'function') {
      console.log('🛒 [PBFCM-PDP] Opening cart drawer');
      toggleCartOpen();

      // Focus the checkout button after cart opens for accessibility
      setTimeout(() => {
        const checkoutButton = document.getElementById('checkoutButton');

        if (checkoutButton && !checkoutButton.disabled) {
          checkoutButton.focus();
          console.log('♿ [PBFCM-PDP] Focused checkout button for accessibility');

          // Announce to screen readers
          if (typeof announceToScreenReader === 'function') {
            announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
          }
        } else {
          // Fallback: focus close button if checkout is disabled
          const cart = document.querySelector('#main-nav');
          const closeButton = cart?.querySelector('.js-menu__close');

          if (closeButton) {
            closeButton.focus();
            console.log('♿ [PBFCM-PDP] Checkout disabled, focused close button');
          }
        }
      }, 150);
    }

    try {
      // Best-effort: fetch the latest cart and render it into the open drawer.
      const cartResponse = await fetch('/cart.js');
      const updatedCart = await cartResponse.json();

      console.log('🛒 [PBFCM-PDP] Updated cart:', updatedCart);

      // Update cart display
      if (typeof updateCartDisplay === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart display');
        updateCartDisplay(updatedCart);
      }

      // Update cart count
      if (typeof updateCartCount === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart count:', updatedCart.item_count);
        updateCartCount(updatedCart.item_count);
      }

      // Dispatch cart:updated event for gift progress and other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      console.log('📢 [PBFCM-PDP] Dispatched cart:updated event');
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error updating cart display:', error);
    }
  }

  /**
   * Set loading state
   * @param {boolean} loading - Whether loading or not
   */
  function setLoading(loading) {
    const btn = document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN);
    const loadingEl = document.getElementById(CONFIG.IDS.CART_LOADING);

    if (btn) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.7' : '1';
      btn.style.cursor = loading ? 'not-allowed' : 'pointer';
    }

    if (loadingEl) {
      loadingEl.style.display = loading ? 'flex' : 'none';
    }
  }

  // Expose public API
  PBFCM_PDP.init = init;
  PBFCM_PDP.selectFrequency = selectFrequency;
  PBFCM_PDP.selectFrequencyOption = selectFrequencyOption;
  PBFCM_PDP.onSubscriptionIntervalChange = onSubscriptionIntervalChange;
  PBFCM_PDP.selectQuantity = selectQuantity;
  PBFCM_PDP.handleQuantityKeydown = handleQuantityKeydown;
  PBFCM_PDP.updateUpsellQuantity = updateUpsellQuantity;
  PBFCM_PDP.calculateTotals = calculateTotals;
  PBFCM_PDP.handleAddToCart = handleAddToCart;
  PBFCM_PDP.refreshUpsellDialogMode = scheduleAddToCartButtonTextUpdate;
  PBFCM_PDP.copyCouponCode = copyCouponCode;
  PBFCM_PDP.showOTPConvertDialog = showOTPConvertDialog;
  PBFCM_PDP.showUpsellDialog = showUpsellDialog;
  PBFCM_PDP.getState = () => ({ ...state }); // For debugging
  PBFCM_PDP.applyLatchedOfferToBuyBox = applyLatchedOfferToBuyBox;

})(window.PBFCM_PDP);

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.PBFCM_PDP.init);
} else {
  window.PBFCM_PDP.init();
}

document.addEventListener('shopify:section:load', function() {
  if (window.PBFCM_PDP && typeof window.PBFCM_PDP.init === 'function') {
    window.PBFCM_PDP.init();
  }
});

document.addEventListener('fd:offer-changed', function () {
  if (window.PBFCM_PDP && typeof window.PBFCM_PDP.applyLatchedOfferToBuyBox === 'function') {
    window.PBFCM_PDP.applyLatchedOfferToBuyBox();
  }
});
